import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart3, BriefcaseBusiness, CircleUserRound, FilePlus2, FolderKanban, Inbox, Info, ShieldCheck } from "lucide-react";
import CockpitShell from "../../components/CockpitShell";
import EmployerStatusBand from "../../components/employer/EmployerStatusBand";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { CCallout, CLoadingButton, CPopover, CProgress, CProgressBar, CSmartPagination, CSmartTable } from "@coreui/react-pro";
import StbAuthorityFilingEmployerWorkspace from "./StbAuthorityFilingEmployerWorkspace";
import {
  useEmployerActionAdapter,
  useEmployerMode,
  useEmployerCoach,
  useEmployerTrainingSession,
} from "../../components/employer/EmployerModeContext";
import { buildCaseModelFromSources } from "../../services/employer/employerCaseOsDomain";

const navItems = [
  { href: "/dashboard/employer", label: "Arbeitsraum", icon: BriefcaseBusiness },
  { href: "/dashboard/employer/jobs", label: "Anzeigen", icon: FilePlus2 },
  { href: "/dashboard/employer/candidates", label: "Kandidaten", icon: Inbox },
  { href: "/dashboard/employer/documents", label: "Dokumente", icon: FolderKanban },
  { href: "/dashboard/employer/compliance", label: "Filing & Compliance", icon: ShieldCheck },
  { href: "/dashboard/employer/analytics", label: "Analytics", icon: BarChart3 },
  { href: "/dashboard/employer/profile", label: "Arbeitgeberprofil", icon: CircleUserRound },
];

const DECISION_STATE_OPTIONS = [
  { value: "new", label: "Neu" },
  { value: "review", label: "In Prüfung" },
  { value: "shortlist", label: "Interessant" },
  { value: "rejected", label: "Abgelehnt" },
  { value: "hired", label: "Eingestellt" },
];

const VERIFICATION_STATUS_LABELS = {
  declared: "Erfasst",
  uploaded: "Hochgeladen",
  pending_review: "Zur Pruefung",
  formally_checked: "Geprueft",
  online_verifiable: "Online pruefbar",
  online_verified: "Online bestaetigt",
  inconsistent: "Widerspruechlich",
  suspicious: "Auffaellig",
  rejected: "Abgelehnt",
};

const VERIFICATION_TYPE_LABELS = {
  language_certificate: "Sprachnachweis",
  education_certificate: "Bildungsnachweis",
  work_reference: "Arbeitsnachweis",
  passport_copy: "Passkopie",
  professional_certificate: "Berufsnachweis",
  other: "Weiteres",
};

const FEDERAL_STATE_OPTIONS = [
  "Baden-Wuerttemberg",
  "Bayern",
  "Berlin",
  "Brandenburg",
  "Bremen",
  "Hamburg",
  "Hessen",
  "Mecklenburg-Vorpommern",
  "Niedersachsen",
  "Nordrhein-Westfalen",
  "Rheinland-Pfalz",
  "Saarland",
  "Sachsen",
  "Sachsen-Anhalt",
  "Schleswig-Holstein",
  "Thueringen",
];

const RECOGNITION_STATUS_OPTIONS = [
  { value: "required", label: "Anerkennung erforderlich" },
  { value: "open", label: "Anerkennung offen" },
  { value: "partial", label: "Anerkennung teilweise" },
  { value: "full", label: "Anerkennung voll" },
  { value: "partnership", label: "Anerkennungspartnerschaft" },
  { value: "not_required", label: "Keine Anerkennungspflicht" },
];

const REGULATED_PROFESSION_OPTIONS = [
  { value: "auto", label: "Automatisch erkennen" },
  { value: "1", label: "Ja, reglementiert" },
  { value: "0", label: "Nein, nicht reglementiert" },
];

const PROCEDURE_OPTIONS = [
  { value: "0", label: "Standardverfahren" },
  { value: "1", label: "Beschleunigtes Fachkraefteverfahren" },
];

const FILING_FIELD_HELP = {
  procedure: {
    title: "Verfahrensart",
    body: "Dieses Feld entscheidet, wie aktiv der Arbeitgeber den Einwanderungspfad steuert.",
    points: [
      "Standardverfahren: regulaerer Anerkennungs-, BA- und Visumpfad ohne fruehe Arbeitgebersteuerung.",
      "Beschleunigtes Verfahren: fruehe Einbindung der Auslaenderbehoerde, oft mit Vollmacht und zusaetzlichen Arbeitgeberunterlagen.",
      "Folge: Experts4Europe zieht andere Stellen, Unterlagen und Signaturpfade in das Paket.",
    ],
  },
  regulated: {
    title: "Reglementierter Beruf",
    body: "Hier wird entschieden, ob fuer die Berufsausuebung eine gesetzliche Anerkennung oder Berufserlaubnis noetig ist.",
    points: [
      "Relevant bei Pflege, Physiotherapie und anderen gesetzlich geschuetzten Berufswegen.",
      "Folge: Landes- oder kommunale Anerkennungsstellen und Zusatzunterlagen werden zwingender Teil des Pakets.",
      "Nicht reglementierte Berufe laufen staerker ueber Arbeitgeber-, BA- und Visumpfade wie Blue Card oder qualifizierte Beschaeftigung.",
    ],
  },
  state: {
    title: "Bundesland und lokale Zustaendigkeit",
    body: "Deutschland arbeitet mit Laenderhoheit. Bundesland und Zielort koennen Verfahren, Stellen und Formulare spuerbar veraendern.",
    points: [
      "Dasselbe Berufsbild kann je Land ueber andere Anerkennungsstellen oder Formulare laufen.",
      "Kommunale Sonderstellen wie Muenchen, Frankfurt oder Hamburg koennen den Landesdefault nochmals ueberlagern.",
      "Folge: Experts4Europe loest Bund, Land und Stadt getrennt auf und markiert aktive Overrides sichtbar.",
    ],
  },
  recognition: {
    title: "Anerkennungsstatus",
    body: "Der Anerkennungsstatus zeigt, wie weit der berufliche Zugang bereits geklaert ist und wie viel Anerkennungsarbeit noch offen bleibt.",
    points: [
      "Voll anerkannt: weniger Anerkennungsdokumente, mehr Fokus auf Beschaeftigung und Visum.",
      "Offen oder teilweise: mehr Nachweise, Pruefpfade und Anerkennungsunterlagen.",
      "Anerkennungspartnerschaft: zusaetzliche Arbeitgeberunterlagen und Qualifizierungspfad moeglich.",
    ],
  },
};

const REVIEW_BUCKET_META = {
  ready_to_sign: {
    label: "Zur Unterschrift bereit",
    tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  ready_to_upload: {
    label: "Zur Einreichung bereit",
    tone: "border-sky-200 bg-sky-50 text-sky-700",
  },
  prepared_but_incomplete: {
    label: "Vorbereitet, aber unvollstaendig",
    tone: "border-amber-200 bg-amber-50 text-amber-700",
  },
  manual_only: {
    label: "Manuell erforderlich",
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  },
  missing_docs: {
    label: "Fehlende Nachweise",
    tone: "border-rose-200 bg-rose-50 text-rose-700",
  },
};

const FRESHNESS_META = {
  current_locally_checked: {
    tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  local_unknown_currency: {
    tone: "border-amber-200 bg-amber-50 text-amber-700",
  },
  local_update_unclear: {
    tone: "border-amber-200 bg-amber-50 text-amber-700",
  },
  officially_described_only: {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  },
  reference_only: {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  },
};

const RESPONSIBILITY_META = {
  employer: {
    tone: "border-sky-200 bg-sky-50 text-sky-700",
  },
  candidate: {
    tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  both: {
    tone: "border-violet-200 bg-violet-50 text-violet-700",
  },
  third_party: {
    tone: "border-amber-200 bg-amber-50 text-amber-700",
  },
  authority: {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  },
  manual: {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  },
};

const TASK_STATUS_LABELS = {
  open: "Offen",
  requested: "Angefragt",
  in_progress: "In Arbeit",
  completed: "Erledigt",
  blocked: "Blockiert",
  waived: "Zurueckgestellt",
};

const REQUEST_STATUS_LABELS = {
  draft: "Entwurf",
  sent: "Gesendet",
  viewed: "Gesehen",
  answered: "Beantwortet",
  fulfilled: "Erfuellt",
  expired: "Abgelaufen",
  obsolete: "Obsolet",
};

const RESPONSE_STATUS_LABELS = {
  response_received: "Antwort eingegangen",
  response_under_review: "In Pruefung",
  response_accepted: "Antwort akzeptiert",
  response_rejected: "Nacharbeit noetig",
  response_incomplete: "Antwort unvollstaendig",
};

function formatDecisionState(value) {
  return DECISION_STATE_OPTIONS.find((option) => option.value === value)?.label || value || "Neu";
}

function formatAdStatusLabel(value) {
  const map = {
    draft: "Entwurf",
    published: "Veroeffentlicht",
    active: "Aktiv",
    paused: "Pausiert",
    expired: "Abgelaufen",
    archived: "Archiviert",
  };
  return map[String(value || "").toLowerCase()] || value || "Unbekannt";
}

function formatAdSelectionLabel(ad) {
  if (!ad) return "Anzeige waehlen";
  const candidateCount = Number(ad.application_count || 0);
  return `#${ad.id} · ${ad.titel} (${candidateCount}) · ${ad.status}`;
}

function formatBoolean(value) {
  return value ? "Ja" : "Nein";
}

function formatLanguageAlignmentState(value) {
  const map = {
    no_shared_language_evidence: "Kein freigegebenes Sprachsignal",
    no_language_evidence: "Keine Sprachbasis",
    certificate_only: "Nur Zertifikat",
    certificate_only_unusable: "Zertifikat noch nicht tragfaehig",
    certificate_not_comparable: "Nicht sicher vergleichbar",
    certificate_not_reliable: "Zertifikat noch nicht belastbar",
    internal_test_only: "Nur interner Test",
    aligned: "Signale konsistent",
    slight_deviation: "Leichte Abweichung",
    internal_below_certificate: "Auffaellige Abweichung",
    internal_above_certificate: "Interner Test staerker",
    stale_or_time_sensitive: "Zeitlich eingeschraenkt",
    inconclusive: "Nicht sicher vergleichbar",
  };
  return map[value] || value || "Nicht sicher vergleichbar";
}

function formatResponseStatus(value) {
  return RESPONSE_STATUS_LABELS[String(value || "").toLowerCase()] || "Noch keine Antwort";
}

function formatDateTime(value) {
  if (!value) return "n/a";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function formatAbsoluteDate(value) {
  if (!value) return "k.A.";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function buildAdditionalActiveApplicationSummary(row) {
  const total = Number(row?.other_active_application_count || 0);
  const sameEmployer = Number(row?.same_employer_other_active_count || 0);
  const external = Number(row?.external_active_application_count || 0);

  if (!total) {
    return {
      total: 0,
      sameEmployer: 0,
      external: 0,
      summary: "Keine weiteren aktiven Bewerbungen",
      detail: "Nur dieser Fall ist aktuell aktiv sichtbar.",
    };
  }

  const detailParts = [];
  if (sameEmployer > 0) detailParts.push(`${sameEmployer} im selben Arbeitgeberkontext`);
  if (external > 0) detailParts.push(`${external} anonymisiert ausserhalb`);

  return {
    total,
    sameEmployer,
    external,
    summary: `Weitere aktive Bewerbungen: ${total}`,
    detail: detailParts.join(" · ") || "Weitere aktive Bewerbung sichtbar",
  };
}

function getAdditionalApplicationProgressColor(entry) {
  const status = String(entry?.status || "").toLowerCase();
  if (String(entry?.visibility || "").toLowerCase() === "external_employer") return "secondary";
  if (status === "shortlist") return "success";
  if (status === "review") return "info";
  return "warning";
}

function estimateApplicationActivityProgress(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "hired") return 100;
  if (normalized === "rejected") return 100;
  if (normalized === "shortlist") return 82;
  if (normalized === "review") return 61;
  return 28;
}

function buildCandidateActivityPreview(row, additionalActiveApplications) {
  const additional = additionalActiveApplications || {};
  const total = Number(additional.total ?? row?.other_active_application_count ?? 0);
  const sameEmployer = Number(additional.same_employer_count ?? row?.same_employer_other_active_count ?? 0);
  const external = Number(additional.external_count ?? row?.external_active_application_count ?? 0);

  const detailEntries = Array.isArray(additional.items)
    ? additional.items.slice(0, 4).map((entry, index) => ({
      key: `detail-${entry.application_id || index}`,
      label: entry.visibility === "external_employer"
        ? `Externer Parallelfall ${index + 1}`
        : (entry.title || `Parallelfall ${index + 1}`),
      note: `${entry.status_label || formatDecisionState(entry.status)} · ${entry.role_label || "Bewerbung aktiv"}`,
      value: Number(entry.progress_percent || 0),
      color: getAdditionalApplicationProgressColor(entry),
    }))
    : [];

  return {
    total,
    sameEmployer,
    external,
    detailEntries,
  };
}

function CandidateActivityPopover({
  candidateRef,
  row,
  additionalActiveApplications,
  popoverId,
  isOpen,
  onOpen = () => {},
  onClose = () => {},
}) {
  if (!candidateRef) return null;
  const preview = buildCandidateActivityPreview(row, additionalActiveApplications);
  const popoverWidth = "min(32rem, calc(100vw - 3rem))";

  if (!preview.total) {
    return <span className="font-semibold text-slate-900">{candidateRef}</span>;
  }

  return (
    <CPopover
      trigger={[]}
      visible={isOpen}
      placement="auto"
      fallbackPlacements={["left", "bottom", "top", "right"]}
      delay={{ show: 120, hide: 80 }}
      className="candidate-activity-popover"
      style={{
        "--cui-popover-max-width": popoverWidth,
        maxWidth: popoverWidth,
      }}
      title="Bewerbungsaktivitaet"
      content={(
        <div
          className="flex flex-col gap-3"
          style={{ width: "min(32rem, calc(100vw - 4rem))" }}
          onMouseEnter={() => onOpen(popoverId)}
          onMouseLeave={() => onClose(popoverId)}
        >
          <div>
            <p className="mb-1 text-sm font-semibold text-slate-900">{candidateRef}</p>
            <p className="mb-0 text-xs text-slate-500">
              {`${preview.total} weitere aktive Bewerbung${preview.total === 1 ? "" : "en"} im sichtbaren Scope`}
            </p>
            {preview.total ? (
              <p className="mt-1 mb-0 text-[11px] text-slate-500">
                {[
                  preview.sameEmployer ? `${preview.sameEmployer} im selben Arbeitgeberkontext` : "",
                  preview.external ? `${preview.external} anonymisiert ausserhalb` : "",
                ].filter(Boolean).join(" · ")}
              </p>
            ) : null}
          </div>

          {preview.detailEntries.length ? (
            <div className="border-top pt-2">
              <p className="mb-2 text-[11px] uppercase tracking-[0.16em] text-slate-400">Sichtbare Parallelfaelle</p>
              <div className="flex flex-col gap-2">
                {preview.detailEntries.map((entry) => (
                  <div key={entry.key}>
                    <div className="mb-1 flex items-center justify-between gap-3 text-xs">
                      <span className="text-slate-700">{entry.label}</span>
                      <span className="text-slate-500">{entry.value}%</span>
                    </div>
                    <CProgress thin>
                      <CProgressBar color={entry.color} value={entry.value} />
                    </CProgress>
                    <p className="mt-1 mb-0 text-[11px] text-slate-500">{entry.note}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : preview.total ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
              Einzelne Parallelfaelle werden angezeigt, sobald die freigegebenen Falldetails geladen sind.
            </div>
          ) : null}
        </div>
      )}
    >
      <span
        className="inline-flex items-center gap-1 rounded text-left font-semibold text-slate-900 transition hover:text-sky-700"
        onMouseEnter={() => onOpen(popoverId)}
        onMouseLeave={() => onClose(popoverId)}
      >
        <span>{candidateRef}</span>
        <BarChart3 className="h-3.5 w-3.5 text-slate-400" />
      </span>
    </CPopover>
  );
}

function formatRelativeDays(value) {
  if (value == null || value === "") return "n/a";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  if (numeric === 0) return "heute";
  if (numeric === 1) return "1 Tag";
  if (numeric > 1) return `${numeric} Tage`;
  if (numeric === -1) return "seit 1 Tag ueberfaellig";
  return `seit ${Math.abs(numeric)} Tagen ueberfaellig`;
}

function formatUrgencyLabel(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "critical" || normalized === "urgent") return "kritisch";
  if (normalized === "high") return "hoch";
  if (normalized === "low") return "niedrig";
  return "mittel";
}

function getDeadlineTone(requestEntry) {
  if (requestEntry?.overdue) return "border-rose-200 bg-rose-50 text-rose-700";
  if (requestEntry?.due_soon) return "border-amber-200 bg-amber-50 text-amber-700";
  if (requestEntry?.fresh_response) return "border-sky-200 bg-sky-50 text-sky-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function buildInitialDecisionForm(detail) {
  return {
    decision_state: detail?.decision_preparation?.decision_state || detail?.application_status || "new",
    decision_note: detail?.decision_preparation?.decision_note || "",
  };
}

function detectDefaultFederalState(selectedAd) {
  if (selectedAd?.standort_ort === "Berlin") return "Berlin";
  if (selectedAd?.standort_ort === "Muenchen" || selectedAd?.standort_ort === "München") return "Bayern";
  return "";
}

function detectInitialRecognitionStatus(selectedAd) {
  const professionText = String(selectedAd?.berufsbezeichnung || selectedAd?.titel || selectedAd?.standort_ort || "").toLowerCase();
  if (/\bit\b|software|entwickler|developer|engineer/.test(professionText)) return "not_required";
  return "required";
}

function buildInitialFilingForm(selectedAd) {
  return {
    federal_state: detectDefaultFederalState(selectedAd),
    city: selectedAd?.standort_ort || "",
    recognition_status: detectInitialRecognitionStatus(selectedAd),
    fast_track: "0",
    regulated_profession: "auto",
  };
}

function hasOwnKey(target, key) {
  return Object.prototype.hasOwnProperty.call(target || {}, key);
}

function normalizeFilingFastTrackValue(value, procedure) {
  if (value === undefined && procedure === undefined) return "";
  const token = String(value ?? "").trim().toLowerCase();
  const procedureToken = String(procedure || "").trim().toLowerCase();
  if (["1", "true", "yes", "ja", "accelerated", "beschleunigt"].includes(token)) return "1";
  if (["0", "false", "no", "nein", "standard"].includes(token)) return "0";
  if (procedureToken === "accelerated") return "1";
  if (procedureToken) return "0";
  return "";
}

function normalizeRegulatedProfessionValue(value) {
  if (value === true) return "1";
  if (value === false) return "0";
  const token = String(value ?? "").trim().toLowerCase();
  if (["1", "true", "yes", "ja"].includes(token)) return "1";
  if (["0", "false", "no", "nein"].includes(token)) return "0";
  return "auto";
}

function buildFilingFormFromProfile(profile, selectedAd) {
  const base = buildInitialFilingForm(selectedAd);
  if (!profile || typeof profile !== "object") return base;
  return {
    federal_state: hasOwnKey(profile, "federal_state") ? String(profile.federal_state || "") : base.federal_state,
    city: hasOwnKey(profile, "city") ? String(profile.city || "") : base.city,
    recognition_status: hasOwnKey(profile, "recognition_status") && profile.recognition_status
      ? String(profile.recognition_status)
      : base.recognition_status,
    fast_track: normalizeFilingFastTrackValue(
      hasOwnKey(profile, "fast_track") ? profile.fast_track : undefined,
      hasOwnKey(profile, "procedure") ? profile.procedure : undefined
    ) || base.fast_track,
    regulated_profession: normalizeRegulatedProfessionValue(
      hasOwnKey(profile, "regulated_profession") ? profile.regulated_profession : undefined
    ) || base.regulated_profession,
  };
}

function createEmptyDetailState(selectedAd = null) {
  return {
    data: null,
    form: { decision_state: "new", decision_note: "" },
    filingForm: buildInitialFilingForm(selectedAd),
    filingData: null,
    filingError: "",
    filingFlash: "",
    filingLoading: false,
    refreshingDocuments: false,
    exporting: false,
    finalizingPack: false,
    taskNotes: {},
    error: "",
    flash: "",
    loading: false,
    saving: false,
  };
}

function formatVerificationSummary(byStatus) {
  const entries = Object.entries(byStatus || {}).filter(([, count]) => Number(count || 0) > 0);
  if (!entries.length) return "Nur Kompaktstatus, keine Rohsicht auf Referenzen.";
  return entries
    .map(([status, count]) => `${VERIFICATION_STATUS_LABELS[status] || status}: ${count}`)
    .join(" · ");
}

function formatVerificationTypeSummary(byType) {
  const entries = Object.entries(byType || {}).filter(([, count]) => Number(count || 0) > 0);
  if (!entries.length) return "Keine typisierten Nachweise freigegeben.";
  return entries
    .map(([type, count]) => `${VERIFICATION_TYPE_LABELS[type] || type}: ${count}`)
    .join(" · ");
}

function makeDownloadName(prefix, extension) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `${prefix}-${stamp}.${extension}`;
}

function FilingInfoHint({ hintKey }) {
  const content = FILING_FIELD_HELP[hintKey];
  if (!content) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 transition hover:border-slate-400 hover:text-slate-700"
          title={content.title}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-slate-900">{content.title}</p>
          <p className="text-sm leading-6 text-slate-600">{content.body}</p>
          {content.points?.length ? (
            <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-600">
              {content.points.map((entry) => (
                <li key={entry}>{entry}</li>
              ))}
            </ul>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function getReviewBucketMeta(bucket) {
  return REVIEW_BUCKET_META[bucket] || {
    label: bucket || "Unbekannt",
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  };
}

function getFreshnessMeta(status) {
  return FRESHNESS_META[status] || {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  };
}

function formatCoverageLevel(value) {
  const labels = {
    high: "hohe Feldabdeckung",
    medium: "mittlere Feldabdeckung",
    low: "niedrige Feldabdeckung",
    none: "keine Feldabdeckung",
  };
  return labels[value] || value || "unbekannt";
}

function formatDelta(value) {
  const numeric = Number(value || 0);
  return `${numeric >= 0 ? "+" : ""}${numeric}`;
}

function formatHistoryAction(actionType) {
  const labels = {
    generated: "Kit erzeugt",
    export_json: "JSON exportiert",
    export_html: "HTML exportiert",
    document_refresh: "Bibliothek aktualisiert",
    task_complete: "Task erledigt",
    task_block: "Task blockiert",
    task_reopen: "Task wieder geoeffnet",
    task_request: "Task als Request markiert",
    task_progress: "Task in Arbeit",
    task_note: "Task-Notiz gespeichert",
    request_created: "Request erstellt",
    request_sent: "Request gesendet",
    request_viewed: "Request gesehen",
    request_answered: "Request beantwortet",
    request_fulfilled: "Request erfuellt",
    request_obsolete: "Request abgeschlossen",
    finalized_pack: "Final Pack erzeugt",
    pack_freeze_for_review: "Pack eingefroren",
    pack_refreeze: "Pack neu eingefroren",
    pack_reopen_pack: "Pack wieder geoeffnet",
    pack_mark_ready_for_signature: "Pack als signaturbereit markiert",
    pack_mark_ready_for_submission: "Pack als einreichungsbereit markiert",
    pack_archive_pack: "Pack archiviert",
  };
  return labels[actionType] || actionType || "Aktion";
}

function formatTaskStatus(value) {
  return TASK_STATUS_LABELS[value] || value || "Offen";
}

function formatRequestStatus(value) {
  return REQUEST_STATUS_LABELS[value] || value || "Entwurf";
}

function getResponsibilityMeta(key) {
  return RESPONSIBILITY_META[key] || {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  };
}

function formatStageLabel(value) {
  if (!value) return "Wiedereinstieg";
  return String(value)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatTrainingResumeSummary(run) {
  if (!run) return null;
  const filingViewLabels = {
    candidate: "Kandidatenansicht",
    documents: "Dokumentenansicht",
    pack: "Pack-Ansicht",
    history: "Historie",
  };
  const parts = [];
  if (run.resume_state?.application_id) parts.push(`Bewerbung ${run.resume_state.application_id}`);
  if (run.resume_state?.filing_view) {
    parts.push(filingViewLabels[run.resume_state.filing_view] || run.resume_state.filing_view);
  }
  if (run.resume_state?.request_id) parts.push(`Request ${run.resume_state.request_id}`);
  return {
    stageLabel: formatStageLabel(run.current_stage),
    summary: parts.length ? parts.join(" · ") : "Trainingslauf an der letzten Arbeitsstelle fortsetzen",
  };
}

function normalizeApplicationRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => (
    row
    && typeof row === "object"
    && !Array.isArray(row)
    && row.id !== undefined
    && row.id !== null
  ));
}

function resolveTrainingNextStep(run) {
  if (!run?.resume_state?.application_id) return null;
  const stage = String(run.current_stage || "");
  const hasActiveRequest = Boolean(run.resume_state?.request_id);
  const defaultTabByStage = {
    request_missing_documents: "candidate",
    candidate_response: "candidate",
    documents_review: "documents",
    filing_preparation: "documents",
    pack_readiness: "pack",
    completed: "history",
    rejected: "history",
    aborted: "history",
  };
  const targetTab = stage === "pack_readiness" && hasActiveRequest
    ? "candidate"
    : (defaultTabByStage[stage] || run.resume_state?.filing_view || "candidate");
  const titleByStage = {
    request_missing_documents: "Fehlende Unterlagen jetzt beim Bewerber anfordern",
    candidate_response: "Rueckmeldung des Bewerbers jetzt pruefen",
    documents_review: "Unterlagenlage jetzt sichten",
    filing_preparation: "Dokumentenlage jetzt fuer das Filing ordnen",
    pack_readiness: "Einreichungspaket jetzt kontrolliert finalisieren",
    completed: "Trainingsfall ist abgeschlossen",
    rejected: "Trainingsfall wurde beendet",
    aborted: "Trainingslauf wurde abgebrochen",
  };
  const detailByStage = {
    request_missing_documents: "Oeffne die Kandidatenanforderungen und fuehre die fehlende Unterlage als Request weiter.",
    candidate_response: "Springe in die Kandidatenanforderungen und entscheide ueber die eingegangene Rueckmeldung.",
    documents_review: "Pruefe zuerst die Dokumentenlage und bringe die offenen Nachweise auf einen belastbaren Stand.",
    filing_preparation: "Arbeite die Dokumente und Formulare geordnet durch, bevor du den Pack-Stand weiterziehst.",
    pack_readiness: hasActiveRequest
      ? "Bevor du das Einreichungspaket finalisierst, musst du die offene Nachforderung im Kandidatenbereich sauber abschliessen."
      : "Wechsle in das Einreichungspaket und fuehre den naechsten Pack-Schritt in der Sandbox aus.",
    completed: "Du kannst den Lauf beenden oder mit einem neuen Szenario neu starten.",
    rejected: "Pruefe den Verlauf oder starte den Trainingslauf neu.",
    aborted: "Pruefe den Verlauf oder starte den Trainingslauf neu.",
  };
  const buttonLabelByTab = {
    candidate: "Zu den Kandidatenanforderungen",
    documents: "Zu Dokumenten und Formularen",
    pack: "Zum Einreichungspaket",
    history: "Zum Verlauf",
  };
  return {
    applicationId: String(run.resume_state.application_id),
    requestId: run.resume_state?.request_id ? String(run.resume_state.request_id) : "",
    targetTab,
    stageLabel: formatStageLabel(stage),
    title: titleByStage[stage] || "Naechsten Trainingsschritt oeffnen",
    detail: detailByStage[stage] || "Die echte Employer-UI fuehrt dich jetzt an die relevante Arbeitsstelle im Trainingsfall.",
    buttonLabel: buttonLabelByTab[targetTab] || "Zum relevanten Abschnitt springen",
  };
}

const StbApplications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ads, setAds] = useState([]);
  const [adId, setAdId] = useState(searchParams.get("adId") || "");
  const requestedApplicationId = searchParams.get("applicationId") || "";
  const requestedRequestId = searchParams.get("request_id") || "";
  const requestedFilingView = searchParams.get("filing_view") || "";
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState("");
  const [expandedId, setExpandedId] = useState("");
  const [queuePage, setQueuePage] = useState(1);
  const [detailsById, setDetailsById] = useState({});
  const [filingTabsById, setFilingTabsById] = useState({});
  const [hoveredCandidatePopoverId, setHoveredCandidatePopoverId] = useState("");
  const applicationCardRefs = useRef({});
  const filingProfileSaveTimersRef = useRef({});
  const candidatePopoverCloseTimerRef = useRef(null);
  const workspaceAdapter = useEmployerActionAdapter();
  const { isTraining, currentRun } = useEmployerMode();
  const { scenarios, startTrainingForJobAd, resumeTraining } = useEmployerTrainingSession();
  const { registerCoachSurface } = useEmployerCoach();
  const selectedAd = ads.find((ad) => String(ad.id) === String(adId)) || null;
  const applicationRows = useMemo(() => normalizeApplicationRows(rows), [rows]);
  const extractKitFromResponse = (response) => response?.kit || response || null;
  const defaultScenarioKey = currentRun?.scenario_key || scenarios?.[0]?.key || "documents_missing";
  const trainingAdId = currentRun?.job_ad_snapshot?.id ? String(currentRun.job_ad_snapshot.id) : "";
  const trainingResume = useMemo(() => (currentRun ? formatTrainingResumeSummary(currentRun) : null), [currentRun]);
  const trainingNextStep = useMemo(() => (currentRun ? resolveTrainingNextStep(currentRun) : null), [currentRun]);
  const activeTrainingDetail = useMemo(() => {
    if (!expandedId) return null;
    return detailsById[String(expandedId)] || null;
  }, [detailsById, expandedId]);
  const activeDetail = activeTrainingDetail;
  const adOptions = isTraining && trainingAdId
    ? ads.filter((entry) => String(entry.id) === trainingAdId)
    : ads;

  const updateDetailState = (applicationId, updater) => {
    const key = String(applicationId);
    setDetailsById((current) => {
      const previous = current[key] || createEmptyDetailState(selectedAd);
      const next = typeof updater === "function" ? updater(previous) : { ...previous, ...updater };
      return { ...current, [key]: next };
    });
  };

  useEffect(() => () => {
    Object.values(filingProfileSaveTimersRef.current).forEach((timerId) => {
      clearTimeout(timerId);
    });
    filingProfileSaveTimersRef.current = {};
  }, []);

  const getActiveFilingTab = (applicationId) => (
    filingTabsById[String(applicationId)]
    || ((["candidate", "documents", "pack", "history"].includes(requestedFilingView) && String(requestedApplicationId) === String(applicationId)) ? requestedFilingView : "candidate")
    || ((requestedRequestId && String(requestedApplicationId) === String(applicationId)) ? "candidate" : "candidate")
  );

  const setActiveFilingTab = (applicationId, nextTab) => {
    setFilingTabsById((current) => ({
      ...current,
      [String(applicationId)]: nextTab,
    }));
  };

  const openCandidatePopover = (popoverId) => {
    if (candidatePopoverCloseTimerRef.current) {
      clearTimeout(candidatePopoverCloseTimerRef.current);
      candidatePopoverCloseTimerRef.current = null;
    }
    setHoveredCandidatePopoverId(String(popoverId || ""));
  };

  const closeCandidatePopover = (popoverId) => {
    if (candidatePopoverCloseTimerRef.current) {
      clearTimeout(candidatePopoverCloseTimerRef.current);
    }
    candidatePopoverCloseTimerRef.current = setTimeout(() => {
      setHoveredCandidatePopoverId((current) => (current === String(popoverId || "") ? "" : current));
      candidatePopoverCloseTimerRef.current = null;
    }, 120);
  };

  useEffect(() => () => {
    if (candidatePopoverCloseTimerRef.current) {
      clearTimeout(candidatePopoverCloseTimerRef.current);
    }
  }, []);

  const registerApplicationCard = (applicationId, node) => {
    const key = String(applicationId);
    if (node) {
      applicationCardRefs.current[key] = node;
    } else {
      delete applicationCardRefs.current[key];
    }
  };

  const scrollToApplicationCard = (applicationId) => {
    const key = String(applicationId);
    const node = applicationCardRefs.current[key];
    if (node?.scrollIntoView) {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const loadAds = async () => {
    const res = await workspaceAdapter.loadAds({ page: 1, limit: 100 });
    const list = res?.rows || [];
    setAds(list);
    if (isTraining && currentRun?.job_ad_snapshot?.id) {
      setAdId(String(currentRun.job_ad_snapshot.id));
      return;
    }
    if (!adId && list[0]?.id) setAdId(String(list[0].id));
  };

  const loadApplications = async (id) => {
    if (!id) return;
    try {
      const res = await workspaceAdapter.loadApplications({ adId: id });
      Object.values(filingProfileSaveTimersRef.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
      filingProfileSaveTimersRef.current = {};
      setRows(normalizeApplicationRows(res?.rows));
      setExpandedId("");
      setDetailsById({});
      setMessage("");
    } catch (e) {
      setMessage(e.response?.data?.message || "Bewerbungen konnten nicht geladen werden.");
      setRows([]);
      setExpandedId("");
      Object.values(filingProfileSaveTimersRef.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
      filingProfileSaveTimersRef.current = {};
      setDetailsById({});
    }
  };

  const loadDecisionSummary = async (applicationId) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      loading: true,
      error: "",
      flash: "",
    }));
    try {
      const res = await workspaceAdapter.loadDecisionSummary({ applicationId });
      updateDetailState(applicationId, {
        data: res,
        form: buildInitialDecisionForm(res),
        filingForm: (detailsById[String(applicationId)]?.filingForm)
          || buildFilingFormFromProfile(res?.filing_profile, selectedAd),
        loading: false,
        saving: false,
        error: "",
        flash: "",
      });
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        loading: false,
        error: e.response?.data?.message || "Entscheidungsuebersicht konnte nicht geladen werden.",
      }));
    }
  };

  const toggleDecisionSummary = async (applicationId) => {
    const key = String(applicationId);
    if (expandedId === key) {
      setExpandedId("");
      return;
    }

    setExpandedId(key);
    const current = detailsById[key];
    if (!current?.data && !current?.loading) {
      await loadDecisionSummary(applicationId);
    }
  };

  const handleDecisionFormChange = (applicationId, field, value) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      form: {
        ...previous.form,
        [field]: value,
      },
      flash: "",
      error: "",
    }));
  };

  const handleFilingFormChange = (applicationId, field, value) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    const nextFilingForm = {
      ...current.filingForm,
      [field]: value,
    };

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingForm: nextFilingForm,
      filingFlash: "",
      filingError: "",
    }));

    const existingTimer = filingProfileSaveTimersRef.current[key];
    if (existingTimer) clearTimeout(existingTimer);
    filingProfileSaveTimersRef.current[key] = setTimeout(async () => {
      delete filingProfileSaveTimersRef.current[key];
      try {
        const res = await workspaceAdapter.saveAuthorityFilingProfile({
          applicationId,
          filingForm: nextFilingForm,
        });
        const savedProfile = res?.input_profile || res?.profile || nextFilingForm;
        updateDetailState(applicationId, (previous) => ({
          ...previous,
          filingForm: buildFilingFormFromProfile(savedProfile, selectedAd),
          filingFlash: previous.filingFlash || "Filing-Klassifikation gespeichert.",
          filingError: "",
        }));
      } catch (e) {
        updateDetailState(applicationId, (previous) => ({
          ...previous,
          filingError: e.response?.data?.message || "Filing-Klassifikation konnte nicht gespeichert werden.",
        }));
      }
    }, 500);
  };

  const handleTaskNoteChange = (applicationId, taskId, value) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      taskNotes: {
        ...previous.taskNotes,
        [String(taskId)]: value,
      },
    }));
  };

  const loadAuthorityFilingKit = async (applicationId) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingLoading: true,
      filingError: "",
      filingFlash: "",
    }));
    try {
      const res = await workspaceAdapter.loadAuthorityFilingKit({
        applicationId,
        filingForm: current.filingForm,
      });
      const kit = extractKitFromResponse(res);
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        filingData: kit,
        filingForm: kit?.input_profile
          ? buildFilingFormFromProfile(kit.input_profile, selectedAd)
          : previous.filingForm,
        filingLoading: false,
        filingFlash: "Filing-Arbeitsraum aktualisiert.",
      }));
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        filingLoading: false,
        filingError: e.response?.data?.message || "Filing-Arbeitsraum konnte nicht geladen werden.",
      }));
    }
  };

  const exportAuthorityFilingKit = async (applicationId, format) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    const suffix = format === "html" ? "html" : "json";
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      exporting: true,
      filingError: "",
    }));
    try {
      await workspaceAdapter.exportAuthorityFilingKit({
        applicationId,
        filingForm: current.filingForm,
        format,
        filename: makeDownloadName(`authority-filing-kit-${applicationId}`, suffix),
      });
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        exporting: false,
        filingFlash: `Download erstellt: authority-filing-kit-${applicationId}`,
      }));
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        exporting: false,
        filingError: e.response?.data?.message || "Export fehlgeschlagen.",
      }));
    }
  };

  const downloadAuthorityDocument = async (applicationId, documentEntry) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      exporting: true,
      filingError: "",
    }));
    try {
      await workspaceAdapter.downloadAuthorityDocument({ applicationId, documentEntry });
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        exporting: false,
        filingFlash: `Dokument bereitgestellt: ${documentEntry?.title || documentEntry?.id || "Dokument"}`,
      }));
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        exporting: false,
        filingError: e.response?.data?.message || "Dokument konnte nicht bereitgestellt werden.",
      }));
    }
  };

  const downloadPreparedAsset = async (applicationId, assetEntry) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      exporting: true,
      filingError: "",
    }));
    try {
      await workspaceAdapter.downloadPreparedAsset({ applicationId, assetEntry });
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        exporting: false,
        filingFlash: `Asset bereitgestellt: ${assetEntry?.file_name || assetEntry?.asset_id || "Asset"}`,
      }));
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        exporting: false,
        filingError: e.response?.data?.message || "Asset konnte nicht bereitgestellt werden.",
      }));
    }
  };

  const applyWorkflowKitResponse = (applicationId, kit, flashMessage) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingData: kit,
      filingForm: kit?.input_profile
        ? buildFilingFormFromProfile(kit.input_profile, selectedAd)
        : previous.filingForm,
      filingLoading: false,
      refreshingDocuments: false,
      finalizingPack: false,
      filingFlash: flashMessage || previous.filingFlash,
      filingError: "",
    }));
  };

  const updateAuthorityTask = async (applicationId, task, action) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingLoading: true,
      filingError: "",
      filingFlash: "",
    }));

    try {
      const res = await workspaceAdapter.updateAuthorityTask({
        applicationId,
        taskId: task.task_id,
        action,
        completionNote: current.taskNotes?.[String(task.task_id)] || task.completion_note || "",
        filingForm: current.filingForm,
      });
      applyWorkflowKitResponse(applicationId, extractKitFromResponse(res), `Task aktualisiert: ${task.title}`);
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        filingLoading: false,
        filingError: e.response?.data?.message || "Task konnte nicht aktualisiert werden.",
      }));
    }
  };

  const createCandidateRequest = async (applicationId, task, sendNow = true) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingLoading: true,
      filingError: "",
      filingFlash: "",
    }));

    try {
      const res = await workspaceAdapter.createCandidateRequest({
        applicationId,
        task,
        sendNow,
        filingForm: current.filingForm,
      });
      applyWorkflowKitResponse(applicationId, extractKitFromResponse(res), `Request gespeichert: ${task.title}`);
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        filingLoading: false,
        filingError: e.response?.data?.message || "Candidate-Request konnte nicht erstellt werden.",
      }));
    }
  };

  const updateCandidateRequest = async (applicationId, requestEntry, status, extra = {}) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingLoading: true,
      filingError: "",
      filingFlash: "",
    }));

    try {
      const res = await workspaceAdapter.updateCandidateRequest({
        applicationId,
        requestId: requestEntry.request_id,
        status,
        extra,
        filingForm: current.filingForm,
      });
      applyWorkflowKitResponse(applicationId, extractKitFromResponse(res), `Request aktualisiert: ${requestEntry.title}`);
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        filingLoading: false,
        filingError: e.response?.data?.message || "Request konnte nicht aktualisiert werden.",
      }));
    }
  };

  const runPackAction = async (applicationId, action, packId = null) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      finalizingPack: true,
      filingError: "",
      filingFlash: "",
    }));

    try {
      const res = await workspaceAdapter.runPackAction({
        applicationId,
        action,
        packId,
        filingForm: current.filingForm,
      });
      const flashLabels = {
        freeze_for_review: "Pack fuer Review eingefroren.",
        refreeze: "Pack kontrolliert neu eingefroren.",
        reopen_pack: "Pack zur Bearbeitung zurueckgefuehrt.",
        mark_ready_for_signature: "Pack als signaturbereit markiert.",
        mark_ready_for_submission: "Pack als einreichungsbereit markiert.",
        archive_pack: "Pack archiviert.",
      };
      applyWorkflowKitResponse(applicationId, extractKitFromResponse(res), flashLabels[action] || "Pack-Aktion gespeichert.");
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        finalizingPack: false,
        filingError: e.response?.data?.message || "Pack-Aktion konnte nicht gespeichert werden.",
      }));
    }
  };

  const finalizeSubmissionPack = async (applicationId) => runPackAction(applicationId, "freeze_for_review");

  const refreshAuthorityDocuments = async (applicationId) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    if (!current.filingData) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        refreshingDocuments: false,
        filingError: "",
        filingFlash: "Erst den Filing-Arbeitsraum erstellen, dann koennen offizielle Dokumente aktualisiert werden.",
      }));
      return;
    }

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      refreshingDocuments: true,
      filingError: "",
      filingFlash: "",
    }));

    try {
      const res = await workspaceAdapter.refreshAuthorityDocuments({
        applicationId,
        filingForm: current.filingForm,
      });
      const kit = extractKitFromResponse(res);
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        refreshingDocuments: false,
        filingData: kit,
        filingFlash: `Offizielle Dokumente aktualisiert: ${kit?.document_refresh?.updated_count || 0} Dateien neu gezogen.`,
      }));
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        refreshingDocuments: false,
        filingError: e.response?.data?.message || "Dokumentenbibliothek konnte nicht aktualisiert werden.",
      }));
    }
  };

  const saveDecisionPreparation = async (applicationId) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState();
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      saving: true,
      error: "",
      flash: "",
    }));

    try {
      const updated = await workspaceAdapter.saveDecisionPreparation({ applicationId, form: current.form });
      setRows((currentRows) => normalizeApplicationRows(currentRows).map((row) => (
        row.id === applicationId
          ? { ...row, status: updated.application_status }
          : row
      )));
      updateDetailState(applicationId, {
        data: updated,
        form: buildInitialDecisionForm(updated),
        loading: false,
        saving: false,
        error: "",
        flash: "Interne Entscheidungsvorbereitung gespeichert.",
      });
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        saving: false,
        error: e.response?.data?.message || "Entscheidung konnte nicht gespeichert werden.",
      }));
    }
  };

  const handleTrainingStart = async () => {
    if (!selectedAd?.id) return;
    try {
      await startTrainingForJobAd(selectedAd.id, defaultScenarioKey);
      setMessage(`Trainingsmodus fuer Anzeige #${selectedAd.id} gestartet.`);
    } catch (e) {
      setMessage(e.response?.data?.message || "Trainingsmodus konnte nicht gestartet werden.");
    }
  };

  const openTrainingStep = async () => {
    if (!trainingNextStep?.applicationId) {
      resumeTraining();
      return;
    }
    const applicationId = Number(trainingNextStep.applicationId);
    const applicationKey = String(trainingNextStep.applicationId);
    const next = new URLSearchParams();
    next.set("adId", String(adId || trainingAdId || currentRun?.job_ad_snapshot?.id || ""));
    next.set("training", "1");
    next.set("applicationId", applicationKey);
    if (trainingNextStep.targetTab) next.set("filing_view", trainingNextStep.targetTab);
    if (trainingNextStep.requestId) next.set("request_id", trainingNextStep.requestId);
    setSearchParams(next);
    setExpandedId(applicationKey);
    setActiveFilingTab(applicationKey, trainingNextStep.targetTab);

    const detailState = detailsById[applicationKey];
    if (!detailState?.data && !detailState?.loading) {
      await loadDecisionSummary(applicationId);
    }
    if (!detailState?.filingData && !detailState?.filingLoading) {
      await loadAuthorityFilingKit(applicationId);
    }

    window.setTimeout(() => {
      scrollToApplicationCard(applicationKey);
    }, 120);
  };

  useEffect(() => {
    document.title = "Experts4Europe | Employer Bewerbungen";
    loadAds().catch(() => setMessage("Anzeigen konnten nicht geladen werden."));
  }, [workspaceAdapter, isTraining, trainingAdId]);

  useEffect(() => {
    if (!isTraining || !trainingAdId) return;
    setAdId(trainingAdId);
  }, [isTraining, trainingAdId]);

  useEffect(() => {
    const activeKit = activeDetail?.filingData || null;
    const activeHandoff = activeDetail?.data || null;
    const activeApplicationId = expandedId ? Number(expandedId) : null;
    const activeRow = applicationRows.find((entry) => String(entry.id) === String(activeApplicationId)) || null;
    const activeCase = activeApplicationId && activeRow
      ? buildCaseModelFromSources({
        row: activeRow,
        detail: activeHandoff,
        filing: activeKit,
        ad: selectedAd,
      })
      : null;
    const activeRequests = activeKit?.candidate_request_board?.all_requests || [];
    const activeDocuments = activeKit?.documents || [];
    const activeTasks = activeKit?.task_board?.active_tasks || [];
    registerCoachSurface({
      screen_key: "employer_applications",
      screen_label: "Bewerbungen",
      ad_id: adId ? Number(adId) : null,
      application_id: activeApplicationId,
      highlighted_step: isTraining
        ? (trainingNextStep?.title || currentRun?.scenario_summary || "")
        : (activeKit?.pack_ops_state?.next_recommended_action?.label
          || activeKit?.submission_pack_state?.next_recommended_action?.label
          || "Sichte den sichtbaren Bewerbungsfall und pruefe offene Blocker."),
      visible_candidate_summary: applicationRows.slice(0, 5).map((row) => ({
        application_id: row.id,
        candidate_ref: row.candidate_ref,
        status: row.status,
        wt_score: row.wt_summary?.score_percent || null,
      })),
      visible_documents: activeDocuments.slice(0, 5).map((entry) => ({
        title: entry.title,
        review_bucket: entry.review_bucket,
        responsibility: entry.responsibility,
      })),
      visible_requests: activeRequests.slice(0, 5).map((entry) => ({
        request_id: entry.request_id,
        title: entry.title,
        status: entry.status,
        urgency: entry.urgency,
      })),
      visible_tasks: activeTasks.slice(0, 5).map((entry) => ({
        task_id: entry.task_id,
        title: entry.title,
        status: entry.status,
        priority: entry.priority,
      })),
      visible_reminders: activeRequests
        .filter((entry) => Number(entry.reminder_count || 0) > 0 || entry.last_reminder_at)
        .slice(0, 5)
        .map((entry) => ({
          request_id: entry.request_id,
          title: entry.title,
          reminder_count: entry.reminder_count || 0,
          last_reminder_at: entry.last_reminder_at || "",
        })),
      filing_status: activeKit ? {
        stage_label: activeKit.submission_pack_state?.current_stage_label,
        blocker_count: activeKit.pack_ops_state?.blocker_count || 0,
        warning_count: activeKit.pack_ops_state?.warning_count || 0,
        progress_percent: activeKit.completion_workflow?.progress_percent || 0,
        case_status: activeCase?.caseStatusLabel || "",
      } : null,
      visible_decision_summary: activeHandoff ? {
        candidate_ref: activeHandoff.candidate_ref,
        application_status: activeHandoff.application_status,
        wt_score: activeHandoff.decision_handoff?.wt_summary?.summary?.score_percent || null,
        language_band: activeHandoff.decision_handoff?.language_test_b1_core?.signal?.estimated_cefr_band || "",
      } : null,
      visible_pack_summary: activeKit ? {
        blocker_count: activeKit.pack_ops_state?.blocker_count || 0,
        warning_count: activeKit.pack_ops_state?.warning_count || 0,
        next_recommended_action: activeKit.pack_ops_state?.next_recommended_action?.label
          || activeKit.submission_pack_state?.next_recommended_action?.label
          || "",
        can_finalize: Boolean(activeKit.submission_pack_state?.can_finalize),
        strict_case_status: activeCase?.caseStatusLabel || "",
      } : null,
      filing_options: activeDetail?.filingForm || null,
      last_visible_action: activeKit?.submission_pack_state?.next_recommended_action?.label
        || activeKit?.pack_ops_state?.next_recommended_action?.label
        || "",
      latest_blocker_reason: activeKit?.pack_ops_state?.dominant_blocker?.detail
        || activeKit?.pack_ops_state?.dominant_blocker?.title
        || "",
      allowed_next_actions: [
        isTraining ? trainingNextStep?.buttonLabel : "",
        activeKit?.submission_pack_state?.next_recommended_action?.label,
        activeKit?.pack_ops_state?.next_recommended_action?.label,
        activeRequests.some((entry) => ["draft", "sent", "viewed"].includes(String(entry.status || "").toLowerCase()))
          ? "Kandidatenanforderungen pruefen"
          : "",
        activeHandoff?.decision_preparation?.decision_state ? "Interne Entscheidungsvorbereitung weiterfuehren" : "",
      ].filter(Boolean),
    });
  }, [registerCoachSurface, applicationRows, activeDetail, trainingNextStep, currentRun, adId, expandedId, isTraining, selectedAd]);

  useEffect(() => {
    if (!adId) return;
    loadApplications(adId);
    const next = new URLSearchParams();
    next.set("adId", adId);
    if (isTraining) next.set("training", "1");
    if (requestedApplicationId) next.set("applicationId", requestedApplicationId);
    if (requestedRequestId) next.set("request_id", requestedRequestId);
    if (requestedFilingView) next.set("filing_view", requestedFilingView);
    setSearchParams(next);
  }, [adId, isTraining, requestedApplicationId, requestedRequestId, requestedFilingView, setSearchParams]);

  useEffect(() => {
    if (!requestedApplicationId) return;
    if (!applicationRows.some((entry) => String(entry.id) === String(requestedApplicationId))) return;
    setExpandedId(String(requestedApplicationId));
    const detail = detailsById[String(requestedApplicationId)];
    if (!detail?.data && !detail?.loading) {
      loadDecisionSummary(Number(requestedApplicationId));
    }
    if (!detail?.filingData && !detail?.filingLoading) {
      loadAuthorityFilingKit(Number(requestedApplicationId));
    }
  }, [requestedApplicationId, applicationRows, detailsById]);

  useEffect(() => {
    if (!isTraining || !trainingNextStep?.applicationId) return;
    if (requestedApplicationId && String(requestedApplicationId) !== String(trainingNextStep.applicationId)) return;
    if (filingTabsById[String(trainingNextStep.applicationId)]) return;
    if (getActiveFilingTab(trainingNextStep.applicationId) === trainingNextStep.targetTab) return;
    setActiveFilingTab(trainingNextStep.applicationId, trainingNextStep.targetTab);
  }, [isTraining, requestedApplicationId, trainingNextStep, filingTabsById, requestedFilingView, requestedRequestId]);

  const employerNotifications = useMemo(() => (
    Object.entries(detailsById || {})
      .flatMap(([applicationId, entry]) => (
        (entry?.filingData?.notification_board?.employer_notifications || []).map((notification) => ({
          ...notification,
          application_id: applicationId,
          ad_id: adId,
        }))
      ))
      .sort((left, right) => new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime())
      .slice(0, 12)
      .map((entry) => ({
        key: `employer-filing-${entry.notification_id}`,
        title: entry.title || "Filing-Hinweis",
        text: entry.message || "",
        href: `/dashboard/employer/candidates?adId=${encodeURIComponent(entry.ad_id || adId || "")}&applicationId=${encodeURIComponent(entry.application_id || "")}&request_id=${encodeURIComponent(entry.request_id || "")}${isTraining ? "&training=1" : ""}`,
      }))
  ), [adId, detailsById, isTraining]);

  const activeApplication = applicationRows.find((entry) => String(entry.id) === String(expandedId)) || null;
  const activeFilingData = activeDetail?.filingData || null;
  const filingOpenPointCount = Number(activeFilingData?.task_board?.open_count || 0) + Number(activeFilingData?.candidate_request_board?.active_requests?.length || 0);
  const applicationQueueRows = useMemo(
    () => applicationRows.map((row) => {
      const detailState = detailsById[String(row.id)] || createEmptyDetailState(selectedAd);
      const filingData = detailState.filingData;
      const additionalApplications = buildAdditionalActiveApplicationSummary(row);
      return {
        id: row.id,
        candidateRef: row.candidate_ref || "CAND-pending",
        candidateSupport: additionalApplications.summary,
        candidateSupportDetail: additionalApplications.detail,
        additionalActiveCount: additionalApplications.total,
        loadedAdditionalActiveApplications: detailState.data?.additional_active_applications || row.additional_active_applications || null,
        statusLabel: formatDecisionState(row.status),
        wtSignal: row.wt_shared && row.wt_summary
          ? `WT ${row.wt_summary.score_percent}%`
          : "Kein geteilter WT",
        filingStage: filingData?.submission_pack_state?.current_stage_label || "Arbeitsraum bei Bedarf",
        updatedAt: formatAbsoluteDate(row.updated_at),
        actionLabel: expandedId === String(row.id) ? "Arbeitsraum schliessen" : "Arbeitsraum oeffnen",
        row,
        _props: expandedId === String(row.id) ? { className: "table-active" } : undefined,
      };
    }),
    [detailsById, expandedId, applicationRows, selectedAd],
  );
  const queuePageSize = 8;
  const queuePageCount = Math.max(1, Math.ceil(applicationQueueRows.length / queuePageSize));
  const pagedApplicationQueueRows = applicationQueueRows.slice((queuePage - 1) * queuePageSize, queuePage * queuePageSize);
  const applicationSurfaceItems = [
    {
      label: "Surface-Typ",
      value: "Operational Review",
      note: "Bewerbungen werden als Review-Liste gefuehrt und oeffnen denselben Arbeitsraum fuer den konkreten Fall.",
    },
    {
      label: "Anzeige im Fokus",
      value: selectedAd ? `#${selectedAd.id}` : "Noch offen",
      note: selectedAd ? `${selectedAd.titel || "Anzeige"} · ${selectedAd.status || "Status offen"}` : "Waehle zuerst die Anzeige, fuer die du Bewerbungen weiterfuehrst.",
    },
    {
      label: "Bewerbungen sichtbar",
      value: `${applicationRows.length}`,
      note: activeApplication ? `Arbeitsraum fuer Bewerbung #${activeApplication.id} ist geoeffnet.` : "Noch kein Einzelfall geoeffnet.",
    },
    {
      label: "Fallstatus",
      value: activeFilingData?.submission_pack_state?.current_stage_label || (isTraining ? "Trainingslauf aktiv" : "Arbeitsraum noch nicht geoeffnet"),
      note: activeFilingData
        ? `${filingOpenPointCount} offene Filing-Punkte im aktuellen Arbeitsraum.`
        : (isTraining ? "Live-UI mit isolierten Trainingsdaten." : "Live-Betrieb ohne geoeffneten Filing-Arbeitsraum."),
      tone: activeFilingData?.completion_workflow?.blocking_count ? "warning" : (activeFilingData ? "success" : undefined),
    },
  ];

  useEffect(() => {
    setQueuePage((current) => Math.min(current, queuePageCount));
  }, [queuePageCount]);

  useEffect(() => {
    const targetId = expandedId || requestedApplicationId;
    if (!targetId) return;
    const targetIndex = applicationQueueRows.findIndex((entry) => String(entry.id) === String(targetId));
    if (targetIndex < 0) return;
    const targetPage = Math.floor(targetIndex / queuePageSize) + 1;
    setQueuePage((current) => (current === targetPage ? current : targetPage));
  }, [applicationQueueRows, expandedId, requestedApplicationId]);

  return (
    <CockpitShell
      title="Kandidaten & Cases"
      subtitle="Die Kandidaten-Surface fuehrt jetzt ueber Case-Status, Vergleich, Dokumente und Filing-Evidenz statt ueber ein isoliertes Bewerbungsmodul."
      badge="Employer Candidate OS"
      navItems={navItems}
      wideLayout
      theme="employer"
      shellLabel="Kandidaten"
      shellDescription="Kandidaten, Scorecards, Dokumente und interne Entscheidungsvorbereitung bleiben im selben Employer Case OS."
      modeLabel={isTraining ? "Trainingsmodus" : "Review-Arbeitsraum"}
      liveLabel="Employer"
      topSignal={isTraining ? "Employer Training" : "Employer System"}
      topContext={isTraining ? "Trainings-Cases" : "Kandidaten"}
      notifications={employerNotifications}
      actions={(
        <>
          {isTraining && currentRun ? (
            <Button
              variant="outline"
              className="dashboard-action-button dashboard-action-button-secondary"
              onClick={openTrainingStep}
            >
              Training fortsetzen
            </Button>
          ) : null}
          {!isTraining && selectedAd ? (
            <Button
              variant="outline"
              className="dashboard-action-button dashboard-action-button-secondary"
              onClick={handleTrainingStart}
            >
              Training fuer Anzeige starten
            </Button>
          ) : null}
          <Button
            variant="outline"
            className="dashboard-action-button dashboard-action-button-secondary"
            onClick={() => loadApplications(adId)}
          >
            {isTraining ? "Training neu laden" : "Refresh"}
          </Button>
        </>
      )}
    >
      <div className="employer-review-page">
        {message ? <div className="dashboard-inline-alert">{message}</div> : null}

        <EmployerStatusBand
          kicker="Applications Workspace"
          title="Bewerbungen, Filing und Entscheidungsvorbereitung laufen in derselben Employer-Surface."
          note="Die wichtigste Employer-Beweisflaeche zeigt jetzt zuerst Anzeige, Fallstand und naechsten Schritt. Technische Tiefe bleibt vorhanden, aber sie ist nicht mehr die erste Lautstaerke."
          items={applicationSurfaceItems}
        />

        <div className="employer-workspace-layout employer-workspace-layout--wide">
          <div className="employer-workspace-main">
            <Card className="dashboard-card-shell">
              <CardHeader>
                <CardTitle className="dashboard-card-title">
                  {isTraining ? "Trainingsanzeige" : "Anzeige auswaehlen"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isTraining && currentRun ? (
                  <div className="employer-selection-note">
                    <strong>{currentRun.job_ad_snapshot?.titel || `Anzeige #${currentRun.job_ad_snapshot?.id}`}</strong>
                    <p>
                      Diese Applications-Surface nutzt dieselbe Employer-UI, aber nur synthetische Bewerber-, Filing- und Nachrichtenpfade
                      fuer den aktiven Trainingsfall.
                    </p>
                  </div>
                ) : (
                  <div className="employer-selection-note">
                    <strong>Live-Anzeige waehlen</strong>
                    <p>Waehle die Anzeige, fuer die du Bewerbungen und Filing-Arbeit im selben Arbeitsraum weiterfuehrst.</p>
                  </div>
                )}
                <Select value={adId} onValueChange={setAdId} disabled={isTraining && Boolean(trainingAdId)}>
                  <SelectTrigger className="dashboard-field max-w-xl">
                    <SelectValue placeholder="Anzeige waehlen" />
                  </SelectTrigger>
                  <SelectContent>
                    {adOptions.map((ad) => (
                      <SelectItem key={ad.id} value={String(ad.id)}>
                        {formatAdSelectionLabel(ad)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!isTraining && selectedAd ? (
                  <div className="employer-live-note">
                    <strong>Live-Betrieb fuer Anzeige #{selectedAd.id}</strong>
                    <p>
                      Keine Trainingsfuehrung, keine synthetischen Bewerber und keine zweite UI. Wenn du denselben Ablauf ueben willst,
                      startest du den Modus gezielt fuer genau diese Anzeige.
                    </p>
                    <div className="employer-live-note__actions">
                      <Button
                        variant="outline"
                        className="dashboard-inline-button"
                        onClick={handleTrainingStart}
                      >
                        Trainingsmodus fuer diese Anzeige starten
                      </Button>
                    </div>
                  </div>
                ) : null}
                {isTraining && trainingResume ? (
                  <div className="employer-live-note">
                    <strong>Wiedereinstieg bereit</strong>
                    <p>{trainingResume.summary}</p>
                    <p>Aktuelle Stage: {trainingResume.stageLabel}. Mit dem Wiedereinstieg springst du direkt an diese Stelle zurueck.</p>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card className="dashboard-card-shell">
              <CardHeader>
                <CardTitle className="dashboard-card-title">
                  {isTraining ? "Synthetische Trainingsbewerbungen" : "Bewerbungen zur Anzeige"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-slate-600">
                {applicationRows.length === 0 ? (
                  <div className="employer-review-empty">
                    <strong>{isTraining ? "Noch keine Trainingsbewerbungen vorhanden." : "Keine Bewerbungen vorhanden."}</strong>
                    <p>Der Arbeitsraum bleibt ruhig, solange kein realer oder synthetischer Fall fuer diese Anzeige vorliegt.</p>
                  </div>
                ) : null}

                {applicationRows.length ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <CCallout color="info" className="mb-4">
                      <strong>Operational Review Queue</strong>
                      <div>Die Queue verdichtet Kandidat, Status, Filing-Kontext und den direkten Einstieg in denselben Arbeitsraum fuer den Fall.</div>
                    </CCallout>
                    <CSmartTable
                      columns={[
                        { key: "candidateRef", label: "Kandidat" },
                        { key: "statusLabel", label: "Status" },
                        { key: "wtSignal", label: "WT / Signal" },
                        { key: "filingStage", label: "Filing" },
                        { key: "updatedAt", label: "Aktualisiert" },
                        { key: "actions", label: "Aktion", sorter: false, filter: false },
                      ]}
                      items={pagedApplicationQueueRows}
                      pagination={false}
                      sorter={false}
                      tableProps={{ hover: true, responsive: true }}
                      scopedColumns={{
                        candidateRef: (item) => (
                          <td>
                            <div className="flex flex-col gap-1">
                              <CandidateActivityPopover
                                candidateRef={item.candidateRef}
                                row={item.row}
                                additionalActiveApplications={item.loadedAdditionalActiveApplications}
                                popoverId={`queue-${item.row.id}`}
                                isOpen={hoveredCandidatePopoverId === `queue-${item.row.id}`}
                                onOpen={openCandidatePopover}
                                onClose={closeCandidatePopover}
                              />
                              <span className="text-xs text-slate-600">{item.candidateSupport}</span>
                              {item.additionalActiveCount ? (
                                <span className="text-[11px] text-slate-500">{item.candidateSupportDetail}</span>
                              ) : null}
                            </div>
                          </td>
                        ),
                        actions: (item) => (
                          <td>
                            <Button
                              variant="outline"
                              className="dashboard-inline-button"
                              onClick={() => toggleDecisionSummary(item.row.id)}
                            >
                              {item.actionLabel}
                            </Button>
                          </td>
                        ),
                      }}
                    />
                    {applicationQueueRows.length > queuePageSize ? (
                      <div className="mt-3 flex justify-end">
                        <CSmartPagination
                          activePage={queuePage}
                          align="end"
                          dots
                          doubleArrows
                          onActivePageChange={setQueuePage}
                          pages={queuePageCount}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

          {!activeApplication ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5">
              <CCallout color="info" className="mb-0">
                <strong>Row-to-Detail aktivieren</strong>
                <div>
                  Waehle in der Queue einen Fall aus. Danach oeffnet sich darunter dieselbe operative Detailflaeche fuer
                  Decision-Handoff, WT-Summary, Filing-Stand und die naechsten zulaessigen Aktionen.
                </div>
              </CCallout>
            </div>
          ) : null}

          {(activeApplication ? [{ row: activeApplication }] : []).map((queueRow) => {
            const row = queueRow.row;
            const detailState = detailsById[String(row.id)] || createEmptyDetailState();
            const detail = detailState.data;
            const decisionPreparation = detail?.decision_preparation || null;
            const detailCandidateRef = detail?.candidate_ref || detail?.decision_handoff?.candidate_ref || row.candidate_ref || "CAND-pending";
            const detailApplicationStatus = detail?.application_status || row.status;
            const candidateCore = detail?.decision_handoff?.candidate_core || {};
            const verification = detail?.decision_handoff?.verification?.references || {};
            const languageSignal = detail?.decision_handoff?.language_test_b1_core || { shared: false };
            const languageAlignment = detail?.decision_handoff?.language_evidence_alignment || null;
            const wtSummary = detail?.decision_handoff?.wt_summary || { shared: false };
            const additionalActiveApplications = detail?.additional_active_applications || {
              total: 0,
              same_employer_count: 0,
              external_count: 0,
              items: [],
            };
            const filingData = detailState.filingData;
            const dominantFilingBlocker = filingData?.pack_ops_state?.dominant_blocker || null;
            const decisionBlockers = [
              ...(Array.isArray(detailState.blockers) ? detailState.blockers : []),
              (dominantFilingBlocker?.detail || dominantFilingBlocker?.title || ""),
            ].filter(Boolean);
            const filingPrimaryActionLabel = filingData ? "Filing-Arbeitsraum aktualisieren" : "Filing-Arbeitsraum erstellen";
            const filingActionContext = filingData
              ? (filingData.pack_ops_state?.next_recommended_action?.label
                || filingData.submission_pack_state?.next_recommended_action?.label
                || "Bestehenden Filing-Stand kontrolliert weiterfuehren.")
              : "Erst den Filing-Arbeitsraum laden, dann Dokumente und Behoerdenpfad kontrolliert ordnen.";
            const strictCase = buildCaseModelFromSources({
              row,
              detail,
              filing: filingData,
              ad: selectedAd,
            });
            const decisionNoteRequired = detailState.form.decision_state !== "new";
            const decisionNoteMissing = decisionNoteRequired && !String(detailState.form.decision_note || "").trim();
            const decisionSaveAllowed = Boolean(detail) && !detailState.loading && !detailState.error && !decisionNoteMissing;
            const strictFinalizeBlocked = strictCase.caseStatus !== "filing_ready";
            const finalizeDisabled = !filingData?.submission_pack_state?.can_finalize || detailState.finalizingPack || strictFinalizeBlocked;
            const evidenceSnapshotItems = [
              {
                label: "Fall jetzt",
                primary: `${detailCandidateRef} · ${strictCase.caseStatusLabel}`,
                secondary: `Filing ${filingData?.submission_pack_state?.current_stage_label || "noch nicht erstellt"} · letzte Aktivitaet ${formatAbsoluteDate(decisionPreparation?.updated_at || row.updated_at)}`,
              },
              {
                label: "Decision Handoff",
                primary: decisionPreparation?.decision_note ? "Interne Einordnung vorhanden" : "Noch keine interne Einordnung",
                secondary: decisionPreparation?.decision_note || "Die Entscheidungsvorbereitung lebt aktuell nur ueber Status und Fachsignale.",
              },
              {
                label: "Tragende Evidenz",
                primary: wtSummary.shared && wtSummary.summary
                  ? `WT ${wtSummary.summary.score_percent}%`
                  : (languageSignal.shared && languageSignal.signal
                    ? `Sprache ${languageSignal.signal.estimated_cefr_band || "freigegeben"}`
                    : (verification.present ? `${verification.count || 0} Nachweise` : "Noch kein starkes Zusatzsignal")),
                secondary: verification.present
                  ? `${verification.passport_copy_present ? "Passkopie liegt vor" : "Passkopie fehlt"} · ${formatVerificationSummary(verification.by_status)}`
                  : "Verification-, Trust- oder Sprachsignal erst bei Bedarf vertiefen.",
              },
              {
                label: "Naechster zulaessiger Schritt",
                primary: decisionBlockers.length ? strictCase.caseStatusLabel : filingActionContext,
                secondary: decisionBlockers[0]
                  || strictCase.nextAction,
              },
            ];
            const compareReadinessItems = [
              {
                label: "Status vs Blocker",
                primary: strictCase.caseStatusLabel,
                secondary: decisionBlockers[0] || strictCase.blockerReasons[0] || "Kein harter Entscheidungsblocker sichtbar.",
              },
              {
                label: "Letzte Aktivitaet vs naechster Blick",
                primary: decisionPreparation?.updated_at
                  ? `Interner Stand ${formatAbsoluteDate(decisionPreparation.updated_at)}`
                  : `Queue zuletzt ${formatAbsoluteDate(row.updated_at)}`,
                secondary: strictCase.nextAction,
              },
              {
                label: "Fall vs Anzeige",
                primary: selectedAd
                  ? `${selectedAd.titel || selectedAd.berufsbezeichnung || "Anzeige"} · ${formatAdStatusLabel(selectedAd.status)}`
                  : "Anzeige nicht geladen",
                secondary: `${strictCase.documentsReady}/${strictCase.mandatoryRequirementCount} Pflichtunterlagen bereit · ${strictCase.consentGapCount} Consent-Luecken.`,
              },
            ];

            return (
              <div
                key={row.id}
                ref={(node) => registerApplicationCard(row.id, node)}
                className={`employer-application-row${
                  isTraining && trainingNextStep?.applicationId === String(row.id) ? " is-highlighted" : ""
                }`}
              >
                <div className="employer-application-row__summary">
                  <div className="employer-application-row__title">
                    <div className="employer-review-row__chips">
                      <span className="employer-signal-chip is-accent">{formatDecisionState(row.status)}</span>
                      {row.wt_shared && row.wt_summary ? (
                        <span className="employer-signal-chip">
                          WT {row.wt_summary.score_percent}% ({row.wt_summary.correct_count}/{row.wt_summary.total_count})
                        </span>
                      ) : (
                        <span className="employer-signal-chip">Kein geteilter WT</span>
                      )}
                      {isTraining && trainingNextStep?.applicationId === String(row.id) ? (
                        <span className="employer-signal-chip is-warning">Trainingsfokus</span>
                      ) : null}
                    </div>
                    <strong className="flex flex-wrap items-center gap-1">
                      <span>Bewerbung #{row.id} ·</span>
                      <CandidateActivityPopover
                        candidateRef={row.candidate_ref || "CAND-pending"}
                        row={row}
                        additionalActiveApplications={detailState.data?.additional_active_applications || null}
                        popoverId={`detail-header-${row.id}`}
                        isOpen={hoveredCandidatePopoverId === `detail-header-${row.id}`}
                        onOpen={openCandidatePopover}
                        onClose={closeCandidatePopover}
                      />
                    </strong>
                    <p>{isTraining ? "Gemeinsame Employer-UI mit isolierter Trainingssimulation." : "Live-Fall in derselben Employer-Surface."}</p>
                  </div>

                  <div className="employer-application-row__support">
                    <small>Zuletzt aktualisiert</small>
                    <strong>{formatAbsoluteDate(row.updated_at)}</strong>
                    <small>Angelegt {formatAbsoluteDate(row.created_at)}</small>
                  </div>

                  <div className="employer-review-row__actions">
                    <Button
                      variant="outline"
                      className="dashboard-inline-button"
                      onClick={() => toggleDecisionSummary(row.id)}
                    >
                      {expandedId === String(row.id) ? "Arbeitsraum schliessen" : "Arbeitsraum oeffnen"}
                    </Button>
                  </div>
                </div>

                {expandedId === String(row.id) ? (
                  <div className="employer-application-row__detail">
                    {detailState.loading ? <p className="text-slate-500">Entscheidungsuebersicht wird geladen...</p> : null}
                    {detailState.error ? (
                      <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                        {detailState.error}
                      </p>
                    ) : null}
                    <CCallout color={detailState.error ? "danger" : (decisionBlockers.length ? "warning" : "info")} className="mb-4">
                      <strong>Decision Handoff</strong>
                      <div>
                        {detailState.error
                          ? "Der Fall konnte noch nicht vollstaendig geladen werden. Pruefe zuerst den aktiven Fehler."
                          : decisionBlockers.length
                            ? `Dieser Fall ist noch nicht entscheidungsreif. ${decisionBlockers[0]}`
                            : filingData
                              ? `Status ${formatDecisionState(row.status)} · Filing-Stufe ${filingData.submission_pack_state?.current_stage_label || "Arbeitsstand"} · WT und Handoff bleiben im selben Review-Raum gekoppelt.`
                              : "Der Fall ist geoeffnet. Lade Handoff und Filing, bevor eine interne Entscheidung vorbereitet oder uebergeben wird."}
                      </div>
                    </CCallout>

                    {detail ? (
                      <div className="space-y-4">
                        <div className="rounded-lg border border-slate-200 bg-white p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Weitere aktive Bewerbungen</p>
                              <p className="mt-2 text-lg font-semibold text-slate-900">
                                {additionalActiveApplications.total
                                  ? `${additionalActiveApplications.total} weitere aktive Bewerbung${additionalActiveApplications.total === 1 ? "" : "en"} sichtbar`
                                  : "Keine weiteren aktiven Bewerbungen sichtbar"}
                              </p>
                              <p className="mt-1 text-sm text-slate-600">
                                {additionalActiveApplications.total
                                  ? `${additionalActiveApplications.same_employer_count || 0} im selben Arbeitgeberkontext · ${additionalActiveApplications.external_count || 0} anonymisiert ausserhalb`
                                  : "Aktuell liegt fuer diesen Bewerber kein weiterer aktiver Parallelfall im sichtbaren Scope."}
                              </p>
                            </div>
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                              Marktdynamik sichtbar, Fremdarbeitgeber bleiben geschuetzt
                            </span>
                          </div>

                          {additionalActiveApplications.items?.length ? (
                            <div className="mt-4 space-y-3">
                              {additionalActiveApplications.items.map((entry) => (
                                <div key={`additional-active-${entry.application_id}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                  <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                    <div>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <strong className="text-sm text-slate-900">{entry.title}</strong>
                                        <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600">
                                          {entry.privacy_label}
                                        </span>
                                        <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600">
                                          {entry.status_label}
                                        </span>
                                      </div>
                                      <p className="mt-1 text-xs text-slate-600">
                                        {entry.role_label} · {entry.location_label}
                                      </p>
                                    </div>
                                    <div className="text-left lg:text-right">
                                      <p className="text-sm font-semibold text-slate-900">{entry.progress_percent}%</p>
                                      <p className="text-xs text-slate-500">Zuletzt aktiv {formatAbsoluteDate(entry.updated_at)}</p>
                                    </div>
                                  </div>

                                  <div className="mt-3">
                                    <div className="mb-1 flex items-center justify-between gap-3 text-xs text-slate-600">
                                      <span>{entry.progress_label}</span>
                                      <span>{entry.progress_reason}</span>
                                    </div>
                                    <CProgress thin>
                                      <CProgressBar
                                        color={getAdditionalApplicationProgressColor(entry)}
                                        value={entry.progress_percent}
                                      />
                                    </CProgress>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-600">
                              Kein weiterer aktiver Parallelfall sichtbar. Sobald weitere aktive Bewerbungen existieren, erscheinen sie hier als kompakte Progress-Zeilen.
                            </div>
                          )}
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Evidence Snapshot</p>
                              <p className="mt-2 text-lg font-semibold text-slate-900">Evidenz, Blocker und naechster Schritt bleiben jetzt in einer kompakten Handoff-Zone gekoppelt.</p>
                            </div>
                            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                              {selectedAd?.titel || selectedAd?.berufsbezeichnung || "Anzeige"} · {formatAdStatusLabel(selectedAd?.status)}
                            </span>
                          </div>
                          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                            {evidenceSnapshotItems.map((item) => (
                              <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{item.label}</p>
                                <p className="mt-2 font-semibold text-slate-900">{item.primary}</p>
                                <p className="mt-1 text-sm text-slate-600">{item.secondary}</p>
                              </div>
                            ))}
                          </div>
                          <CCallout color={decisionBlockers.length ? "warning" : "info"} className="mt-4 mb-0">
                            <strong>Evidence -> Blocker -> Next Action</strong>
                            <div>
                              {decisionBlockers.length
                                ? `Blockiert aktuell durch ${decisionBlockers[0]}. Bis dahin bleibt die Entscheidungsvorbereitung intern und der naechste sinnvolle Schritt ist vorbereitend.`
                                : `Die Entscheidungsvorbereitung ist lesbar gekoppelt. Naechster sinnvoller Schritt: ${filingActionContext}`}
                            </div>
                          </CCallout>
                        </div>

                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                          {compareReadinessItems.map((item) => (
                            <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-3">
                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{item.label}</p>
                              <p className="mt-2 font-semibold text-slate-900">{item.primary}</p>
                              <p className="mt-1 text-sm text-slate-600">{item.secondary}</p>
                            </div>
                          ))}
                        </div>

                        <EmployerStatusBand
                          kicker="Fallstatus"
                          title={`Bewerbung #${detail.application_id} bleibt jetzt als gefuehrter Employer-Arbeitsraum lesbar.`}
                          note="Der Status zeigt zuerst, ob der Fall pruefbar ist, welche Filing-Reife vorliegt und welche Folgeaktion als naechstes ansteht."
                          items={[
                            {
                              label: "Entscheidungsstand",
                              value: formatDecisionState(detail.application_status),
                              note: detail.decision_preparation?.updated_at
                                ? `Letzte interne Aktualisierung ${formatAbsoluteDate(detail.decision_preparation.updated_at)}`
                                : "Noch keine interne Entscheidung gespeichert.",
                            },
                            {
                              label: "Verification",
                              value: verification.present ? `${verification.count || 0} Nachweise` : "Noch keine Nachweise",
                              note: verification.passport_copy_present ? "Passkopie liegt vor." : "Passkopie fehlt oder ist nicht freigegeben.",
                              tone: verification.present ? "success" : "warning",
                            },
                            {
                              label: "Sprachsignal",
                              value: languageSignal.shared && languageSignal.signal
                                ? (languageSignal.signal.estimated_cefr_band || "Freigegeben")
                                : "Nicht freigegeben",
                              note: languageAlignment ? formatLanguageAlignmentState(languageAlignment.alignment_state) : "Kein Abgleich sichtbar.",
                            },
                            {
                              label: "Filing",
                              value: detailState.filingData?.submission_pack_state?.current_stage_label || "Noch nicht erstellt",
                              note: detailState.filingData
                                ? `${(detailState.filingData.task_board?.open_count || 0) + (detailState.filingData.candidate_request_board?.active_requests?.length || 0)} offene Filing-Punkte`
                                : "Arbeitsraum wird erst nach Bedarf geladen.",
                              tone: detailState.filingData?.completion_workflow?.blocking_count ? "warning" : (detailState.filingData ? "success" : undefined),
                            },
                          ]}
                        />

                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Application</p>
                            <p className="mt-2 text-slate-900">#{detail.application_id}</p>
                            <div className="mt-1">
                              <CandidateActivityPopover
                                candidateRef={detail.candidate_ref || "CAND-pending"}
                                row={row}
                                additionalActiveApplications={additionalActiveApplications}
                                popoverId={`detail-card-${row.id}`}
                                isOpen={hoveredCandidatePopoverId === `detail-card-${row.id}`}
                                onOpen={openCandidatePopover}
                                onClose={closeCandidatePopover}
                              />
                            </div>
                            <p className="mt-1 text-sky-700">{formatDecisionState(detail.application_status)}</p>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Candidate Core</p>
                            <p className="mt-2 text-slate-700">Profilbasis: {formatBoolean(candidateCore.profile_basis_ready)}</p>
                            <p className="text-slate-700">CV-Kern: {formatBoolean(candidateCore.cv_core_ready)}</p>
                            <p className="mt-2 text-slate-600">
                              Skills {candidateCore.skills?.count || 0} · Sprachen {candidateCore.languages?.count || 0}
                            </p>
                            <p className="text-slate-600">
                              Ausbildung {candidateCore.education?.count || 0} · Erfahrung {candidateCore.experience?.count || 0}
                            </p>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Verification</p>
                            <p className="mt-2 text-slate-700">Nachweise vorhanden: {formatBoolean(verification.present)}</p>
                            <p className="text-slate-700">Referenzen gesamt: {verification.count || 0}</p>
                            <p className="mt-2 text-slate-600">{formatVerificationSummary(verification.by_status)}</p>
                            <p className="mt-2 text-slate-600">{formatVerificationTypeSummary(verification.by_type)}</p>
                            <p className="mt-2 text-slate-700">Passkopie vorhanden: {formatBoolean(verification.passport_copy_present)}</p>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">WT-Zusammenfassung</p>
                            {wtSummary.shared && wtSummary.summary ? (
                              <>
                                <p className="mt-2 text-slate-700">Geteilt: Ja</p>
                                <p className="text-sky-700">
                                  {wtSummary.summary.score_percent}% ({wtSummary.summary.correct_count}/{wtSummary.summary.total_count})
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="mt-2 text-slate-700">Geteilt: Nein</p>
                                <p className="text-slate-600">WT bleibt optionales Zusatzsignal.</p>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4">
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Language Test B1 Core</p>
                          {languageSignal.shared && languageSignal.signal ? (
                            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Band</p>
                                <p className="mt-2 text-xl font-semibold text-slate-900">{languageSignal.signal.estimated_cefr_band || "n/a"}</p>
                                <p className="mt-1 text-slate-600">Gesamtwert {languageSignal.signal.overall_language_score || "k.A."}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Test</p>
                                <p className="mt-2 text-slate-900">{languageSignal.signal.test_type || "k.A."}</p>
                                <p className="mt-1 text-slate-600">Pruefstand {languageSignal.signal.review_status || "k.A."}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Verlauf</p>
                                <p className="mt-2 text-slate-900">{languageSignal.signal.progress?.trend || "Basisstand"}</p>
                                <p className="mt-1 text-slate-600">Aenderung {languageSignal.signal.progress?.delta ?? "k.A."}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Module</p>
                                <p className="mt-2 text-slate-600">
                                  Lesen {languageSignal.signal.module_scores?.reading ?? "k.A."} · Hoeren {languageSignal.signal.module_scores?.listening ?? "k.A."}
                                </p>
                                <p className="text-slate-600">
                                  Bausteine {languageSignal.signal.module_scores?.language_elements ?? "k.A."} · Schreiben {languageSignal.signal.module_scores?.writing ?? "k.A."} · Sprechen {languageSignal.signal.module_scores?.speaking ?? "k.A."}
                                </p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3 md:col-span-2 xl:col-span-4">
                                <p className="text-slate-700">{languageSignal.signal.summary?.summary || "Keine kompakte Sprachsummary vorhanden."}</p>
                                {languageSignal.signal.summary?.caution ? (
                                  <p className="mt-2 text-amber-700">{languageSignal.signal.summary.caution}</p>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <p className="mt-3 text-slate-600">
                              Kein freigegebenes B1-Core-Sprachsignal vorhanden. Keine Rohtexte, keine Transkripte und keine Detailrubrics im Employer-Pfad.
                            </p>
                          )}
                        </div>

                        <div className="rounded-lg border border-sky-200 bg-sky-50/70 p-4">
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Sprachsignal-Einordnung</p>
                          {languageAlignment ? (
                            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Abgleich</p>
                                <p className="mt-2 text-slate-900">{formatLanguageAlignmentState(languageAlignment.alignment_state)}</p>
                                <p className="mt-1 text-slate-600">Sicherheit {languageAlignment.alignment_confidence || "k.A."}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Interner Test</p>
                                <p className="mt-2 text-slate-900">
                                  {languageAlignment.internal_test?.shared && languageAlignment.internal_test?.signal
                                    ? `${languageAlignment.internal_test.signal.estimated_cefr_band || "k.A."} · ${languageAlignment.internal_test.signal.overall_language_score || "k.A."}`
                                    : "Nicht freigegeben"}
                                </p>
                                <p className="mt-1 text-slate-600">
                                  {languageAlignment.internal_test?.shared ? "Freigegebenes B1-Signal" : "Kein freigegebenes B1-Signal"}
                                </p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Zertifikat</p>
                                <p className="mt-2 text-slate-900">
                                  {languageAlignment.certificate?.summary?.claimed_cefr_level || "Kein Zertifikat"}
                                </p>
                                <p className="mt-1 text-slate-600">
                                  {languageAlignment.certificate?.summary?.verification_status
                                    ? `Status ${languageAlignment.certificate.summary.verification_status}`
                                    : "Kein belastbarer Zertifikatsstand"}
                                </p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Trust-Hinweis</p>
                                <p className="mt-2 text-slate-900">
                                  {languageAlignment.contradiction_flag
                                    ? "Erneute Sprachpruefung empfohlen"
                                    : languageAlignment.staleness_flag
                                      ? "Zeitlich nur eingeschraenkt belastbar"
                                      : "Kein harter Widerspruch sichtbar"}
                                </p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3 md:col-span-2 xl:col-span-4">
                                <p className="text-slate-700">{languageAlignment.summary_de}</p>
                                <p className="mt-2 text-slate-600">
                                  Keine Rohdokumente, keine Volluebersetzungen und keine internen AI-Details im Arbeitgeberpfad.
                                </p>
                              </div>
                            </div>
                          ) : (
                            <p className="mt-3 text-slate-600">
                              Noch keine kompakte Sprach-Trust-Summary verfuegbar.
                            </p>
                          )}
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Authority Filing / Immigration Preparation</p>
                              <p className="mt-2 text-slate-600">
                                Erstellt ein arbeitgeberfaehiges Behoerdenpaket mit Bundes-, Landes- und Stadtlogik. Tiefe Referenzpfade liegen aktuell fuer Berlin, Bayern/Muenchen, Nordrhein-Westfalen/Koeln, Baden-Wuerttemberg/Stuttgart, Hessen/Darmstadt-Frankfurt und Hamburg vor.
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant="outline"
                                className="dashboard-inline-button"
                                onClick={() => exportAuthorityFilingKit(row.id, "json")}
                                disabled={detailState.exporting}
                              >
                                JSON Export
                              </Button>
                              <Button
                                variant="outline"
                                className="dashboard-inline-button"
                                onClick={() => exportAuthorityFilingKit(row.id, "html")}
                                disabled={detailState.exporting}
                              >
                                HTML Export
                              </Button>
                            </div>
                          </div>

                          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                            <div>
                              <label className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                                <span>Bundesland</span>
                                <FilingInfoHint hintKey="state" />
                              </label>
                              <Select
                                value={detailState.filingForm.federal_state || "__none__"}
                                onValueChange={(value) => handleFilingFormChange(row.id, "federal_state", value === "__none__" ? "" : value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Bundesland waehlen" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">Noch nicht festgelegt</SelectItem>
                                  {FEDERAL_STATE_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                      {option}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <label className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                                <span>Zielort / Stadt</span>
                                <FilingInfoHint hintKey="state" />
                              </label>
                              <Input
                                value={detailState.filingForm.city || ""}
                                onChange={(event) => handleFilingFormChange(row.id, "city", event.target.value)}
                                placeholder="z. B. Muenchen"
                                className="dashboard-field"
                              />
                            </div>

                            <div>
                              <label className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                                <span>Anerkennungsstatus</span>
                                <FilingInfoHint hintKey="recognition" />
                              </label>
                              <Select
                                value={detailState.filingForm.recognition_status}
                                onValueChange={(value) => handleFilingFormChange(row.id, "recognition_status", value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Anerkennungsstatus" />
                                </SelectTrigger>
                                <SelectContent>
                                  {RECOGNITION_STATUS_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <label className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                                <span>Reglementierter Beruf</span>
                                <FilingInfoHint hintKey="regulated" />
                              </label>
                              <Select
                                value={detailState.filingForm.regulated_profession}
                                onValueChange={(value) => handleFilingFormChange(row.id, "regulated_profession", value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Automatik oder Override" />
                                </SelectTrigger>
                                <SelectContent>
                                  {REGULATED_PROFESSION_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <label className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                                <span>Verfahrensart</span>
                                <FilingInfoHint hintKey="procedure" />
                              </label>
                              <Select
                                value={detailState.filingForm.fast_track}
                                onValueChange={(value) => handleFilingFormChange(row.id, "fast_track", value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Standard oder beschleunigt" />
                                </SelectTrigger>
                                <SelectContent>
                                  {PROCEDURE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <CCallout color={decisionBlockers.length ? "warning" : "info"} className="mb-0 flex-1">
                              <strong>Action Hierarchy</strong>
                              <div>
                                Primaer: {filingPrimaryActionLabel}. Sekundaer: offizielle Dokumente nur dann aktualisieren, wenn Quellen oder Vorgaben geaendert wurden.
                              </div>
                              <div className="mt-2">
                                {decisionBlockers.length
                                  ? `Aktuell vor allem diese Huerde mitlesen: ${decisionBlockers[0]}`
                                  : "Der Filing-Arbeitsraum bleibt die vorbereitende Arbeitsflaeche. Er ersetzt keine spaetere Behoerdeneinreichung."}
                              </div>
                            </CCallout>
                            <div className="flex flex-wrap gap-2">
                              <CLoadingButton
                                variant="outline"
                                className="dashboard-inline-button"
                                disabled={!filingData}
                                disabledOnLoading
                                loading={detailState.refreshingDocuments}
                                title={filingData ? "Offizielle Dokumente aus der Bibliothek neu synchronisieren" : "Erst Filing-Arbeitsraum erstellen"}
                                onClick={() => refreshAuthorityDocuments(row.id)}
                              >
                                {detailState.refreshingDocuments ? "Bibliothek aktualisiert..." : "Offizielle Dokumente aktualisieren"}
                              </CLoadingButton>
                              <CLoadingButton
                                className="dashboard-action-button"
                                disabledOnLoading
                                loading={detailState.filingLoading}
                                onClick={() => loadAuthorityFilingKit(row.id)}
                              >
                                {detailState.filingLoading ? "Arbeitsraum wird erstellt..." : filingPrimaryActionLabel}
                              </CLoadingButton>
                            </div>
                          </div>

                          {detailState.filingError ? (
                            <CCallout color="danger" className="mt-3 mb-0">
                              {detailState.filingError}
                            </CCallout>
                          ) : null}
                          {detailState.filingFlash ? <CCallout color="success" className="mt-3 mb-0">{detailState.filingFlash}</CCallout> : null}

                          {filingData ? (
                            <>
                              <div id={`training-filing-${row.id}`}>
                                <StbAuthorityFilingEmployerWorkspace
                                  row={row}
                                  filingData={filingData}
                                  detailState={detailState}
                                  requestedRequestId={requestedRequestId}
                                  trainingGuidance={isTraining && trainingNextStep?.applicationId === String(row.id) ? trainingNextStep : null}
                                  activeTab={getActiveFilingTab(row.id)}
                                  onTabChange={(nextTab) => setActiveFilingTab(row.id, nextTab)}
                                  onExportKit={exportAuthorityFilingKit}
                                  onFinalizeSubmissionPack={finalizeSubmissionPack}
                                  onRunPackAction={runPackAction}
                                  onDownloadPreparedAsset={downloadPreparedAsset}
                                  onDownloadAuthorityDocument={downloadAuthorityDocument}
                                  onUpdateAuthorityTask={updateAuthorityTask}
                                  onCreateCandidateRequest={createCandidateRequest}
                                  onUpdateCandidateRequest={updateCandidateRequest}
                                  onTaskNoteChange={handleTaskNoteChange}
                                  getDeadlineTone={getDeadlineTone}
                                  getFreshnessMeta={getFreshnessMeta}
                                  getResponsibilityMeta={getResponsibilityMeta}
                                  getReviewBucketMeta={getReviewBucketMeta}
                                  formatCoverageLevel={formatCoverageLevel}
                                  formatDateTime={formatDateTime}
                                  formatDelta={formatDelta}
                                  formatHistoryAction={formatHistoryAction}
                                  formatRequestStatus={formatRequestStatus}
                                  formatResponseStatus={formatResponseStatus}
                                  formatTaskStatus={formatTaskStatus}
                                  formatUrgencyLabel={formatUrgencyLabel}
                                />
                              </div>
                              {false ? (
                            <div className="mt-4 space-y-4">
                              <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-sky-50 p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Employer Ready Filing Pack</p>
                                    <p className="mt-2 text-xl font-semibold text-slate-900">{filingData.employer_ready_pack?.cover_title || filingData.filing_packet_summary?.headline}</p>
                                    <p className="mt-2 text-slate-600">
                                      {filingData.classification?.procedure_label} · {filingData.classification?.federal_state}
                                      {filingData.classification?.city ? ` / ${filingData.classification.city}` : ""}
                                      {" · "}
                                      {filingData.classification?.regulated_profession ? "reglementierter Beruf" : "nicht reglementierter Beruf"}
                                    </p>
                                    <p className="mt-2 text-sm text-slate-600">
                                      {filingData.review_workflow?.overall_label || "Pack-Reifegrad wird berechnet"} ·{" "}
                                      {filingData.document_library_status?.current_locally_checked_documents || 0} lokal gepruefte offizielle Dokumente
                                    </p>
                                  </div>
                                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                                    <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Zur Unterschrift bereit</p>
                                      <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.review_workflow?.counts?.ready_to_sign || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Zur Einreichung bereit</p>
                                      <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.review_workflow?.counts?.ready_to_upload || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Vorbefuellte Originale</p>
                                      <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.fillability_summary?.fillable_pdf || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Transferhilfen</p>
                                      <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.fillability_summary?.structured_transfer_only || 0}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Verfahren</p>
                                  <p className="mt-2 text-slate-900">{filingData.classification?.procedure_explainer?.label || filingData.classification?.pathway_title}</p>
                                  <p className="mt-1 text-slate-600">{filingData.classification?.procedure_explainer?.why_it_fits || filingData.classification?.visa_path}</p>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Ort</p>
                                  <p className="mt-2 text-slate-900">{filingData.classification?.federal_state}</p>
                                  <p className="mt-1 text-slate-600">{filingData.classification?.city || "noch offen"}</p>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Reglementierung</p>
                                  <p className="mt-2 text-slate-900">{filingData.classification?.regulation_explainer?.label || (filingData.classification?.regulated_profession ? "Reglementiert" : "Nicht reglementiert")}</p>
                                  <p className="mt-1 text-slate-600">Anerkennung: {filingData.classification?.recognition_status_label || filingData.classification?.recognition_status}</p>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Bibliothek</p>
                                  <p className="mt-2 text-slate-900">Lokal vorhanden: {filingData.document_library_status?.locally_available_documents || 0}</p>
                                  <p className="mt-1 text-slate-600">Refresh-faehig: {filingData.document_library_status?.refreshable_documents || 0}</p>
                                  <p className="mt-1 text-slate-600">Fillable: {filingData.document_library_status?.fillable_pdf_documents || 0} · Transfer: {filingData.document_library_status?.structured_transfer_documents || 0}</p>
                                  <p className="mt-1 text-slate-600">Geprueft: {filingData.document_library_status?.current_locally_checked_documents || 0} · unklar: {filingData.document_library_status?.local_unknown_currency_documents || 0}</p>
                                </div>
                              </div>

                              <div className="grid gap-4 xl:grid-cols-2">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Verfahrensfolgen</p>
                                  <p className="mt-3 text-sm text-slate-700">{filingData.procedure_difference?.why_it_fits}</p>
                                  <ul className="mt-3 list-disc pl-5 text-sm text-slate-600">
                                    {(filingData.procedure_difference?.practical_effects || []).map((entry) => (
                                      <li key={entry}>{entry}</li>
                                    ))}
                                  </ul>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Reglementierung</p>
                                  <p className="mt-3 text-sm text-slate-700">{filingData.regulation_difference?.why_it_fits}</p>
                                  <ul className="mt-3 list-disc pl-5 text-sm text-slate-600">
                                    {(filingData.regulation_difference?.practical_effects || []).map((entry) => (
                                      <li key={entry}>{entry}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4">
                                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Filing Packet Summary</p>
                                {filingData.filing_packet_summary?.summary?.map((line) => (
                                  <p key={line} className="mt-2 text-slate-700">{line}</p>
                                ))}
                              </div>

                              <div className="grid gap-4 xl:grid-cols-[1.35fr,0.95fr]">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                    <div>
                                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Employer Task Board</p>
                                      <p className="mt-2 text-lg font-semibold text-slate-900">
                                        {filingData.task_board?.open_count || 0} offene Aufgaben · {filingData.task_board?.completed_count || 0} erledigt
                                      </p>
                                      <p className="mt-1 text-sm text-slate-600">
                                        {filingData.submission_pack_state?.current_stage_label || "Arbeitsstand"} · {filingData.submission_pack_state?.stage_reason}
                                      </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                        Signierbar {filingData.task_board?.signable_count || 0}
                                      </span>
                                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                        Uploadbar {filingData.task_board?.uploadable_count || 0}
                                      </span>
                                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                        Blockiert {filingData.task_board?.blocked_count || 0}
                                      </span>
                                    </div>
                                  </div>
                                  {filingData.task_board?.summary_lines?.length ? (
                                    <ul className="mt-3 list-disc pl-5 text-sm text-slate-700">
                                      {filingData.task_board.summary_lines.map((line) => (
                                        <li key={line}>{line}</li>
                                      ))}
                                    </ul>
                                  ) : null}
                                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                    {(filingData.task_board?.lanes || []).map((lane) => (
                                      <div key={`lane-${lane.owner_role}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <div className="flex items-center justify-between gap-2">
                                          <p className="font-semibold text-slate-900">{lane.label}</p>
                                          <span className={`rounded-full border px-2 py-1 text-[11px] ${getResponsibilityMeta(lane.owner_role).tone}`}>
                                            {lane.open_count} offen
                                          </span>
                                        </div>
                                        <p className="mt-2 text-xs text-slate-500">
                                          Ready now {lane.ready_now_count || 0} · Blocker {lane.blocking_count || 0}
                                        </p>
                                        <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                          {(lane.tasks || []).slice(0, 4).map((task) => (
                                            <li key={`lane-task-${task.task_id}`}>{task.title}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Final Submission Pack</p>
                                  <p className="mt-2 text-lg font-semibold text-slate-900">
                                    {filingData.submission_pack_state?.current_stage_label || "Draft Pack"}
                                  </p>
                                  <p className="mt-1 text-sm text-slate-600">{filingData.submission_pack_state?.stage_reason}</p>
                                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Open</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.open_task_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Requests</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.active_request_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Finale Pakete</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.final_submission_packs?.length || 0}</p>
                                    </div>
                                  </div>
                                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-amber-700">Due soon</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.due_soon_request_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-rose-700">Overdue</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.overdue_request_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-sky-700">Notifications</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.notification_count || 0}</p>
                                    </div>
                                  </div>
                                  <div className="mt-4 flex flex-wrap gap-2">
                                    <Button
                                      className="dashboard-action-button"
                                      onClick={() => finalizeSubmissionPack(row.id)}
                                      disabled={finalizeDisabled}
                                    >
                                      {detailState.finalizingPack ? "Pack wird finalisiert..." : "Pack finalisieren"}
                                    </Button>
                                    {!filingData.submission_pack_state?.can_finalize ? (
                                      <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                                        Finalisierung erst ohne blockierende Aufgaben und kritische ueberfaellige Anfragen sinnvoll.
                                      </span>
                                    ) : null}
                                    {strictFinalizeBlocked ? (
                                      <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                                        Case OS sperrt Finalisierung: {strictCase.caseStatusLabel} · {strictCase.nextAction}
                                      </span>
                                    ) : null}
                                  </div>
                                  {filingData.final_submission_packs?.length ? (
                                    <div className="mt-4 space-y-3">
                                      {filingData.final_submission_packs.slice(0, 3).map((pack) => (
                                        <div key={`final-pack-${pack.final_pack_id}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                          <div className="flex flex-wrap items-center justify-between gap-2">
                                            <p className="font-semibold text-slate-900">Pack {pack.pack_no} · {pack.stage_label || pack.stage}</p>
                                            <span className="text-xs text-slate-500">{pack.created_at}</span>
                                          </div>
                                          <div className="mt-2 flex flex-wrap gap-2">
                                            {(pack.pack_assets || []).map((asset) => (
                                              <Button
                                                key={`final-pack-asset-${asset.asset_id}`}
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => downloadPreparedAsset(row.id, asset)}
                                                disabled={detailState.exporting}
                                              >
                                                {asset.file_name}
                                              </Button>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Version Compare</p>
                                    <p className="mt-2 text-lg font-semibold text-slate-900">
                                      {filingData.version_compare?.summary_lines?.length
                                        ? "Aenderungen seit der letzten Version"
                                        : "Noch keine Vorversion fuer den Vergleich"}
                                    </p>
                                    <p className="mt-1 text-sm text-slate-600">
                                      Sichtbar werden Dokument-, Missing-Doc-, Reife-, Behoerden- und Bibliotheksaenderungen, ohne rohe JSON-Diffs offenzulegen.
                                    </p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                    Compare: <span className="font-semibold text-slate-900">{filingData.version_compare ? "letzte Version vs. aktuell" : "erste Version"}</span>
                                  </div>
                                </div>
                                {filingData.version_compare?.summary_lines?.length ? (
                                  <>
                                    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <ul className="list-disc pl-5 text-sm text-slate-700">
                                        {filingData.version_compare.summary_lines.map((line) => (
                                          <li key={line}>{line}</li>
                                        ))}
                                      </ul>
                                    </div>
                                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Dokumente</p>
                                        <p className="mt-2 text-sm text-slate-900">Neu {formatDelta(filingData.version_compare.document_changes?.added?.length || 0)} · Entfallen {formatDelta(-1 * (filingData.version_compare.document_changes?.removed?.length || 0))}</p>
                                        <p className="mt-1 text-xs text-slate-500">Geaendert: {filingData.version_compare.changed_documents?.length || 0}</p>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Fehlende Nachweise</p>
                                        <p className="mt-2 text-sm text-slate-900">Delta {formatDelta(filingData.version_compare.missing_docs_delta)}</p>
                                        <p className="mt-1 text-xs text-slate-500">Geschlossen {(filingData.version_compare.missing_document_changes?.removed || []).length} · Neu {(filingData.version_compare.missing_document_changes?.added || []).length}</p>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Pruefung / Fortschritt</p>
                                        <p className="mt-2 text-sm text-slate-900">Zur Unterschrift {formatDelta(filingData.version_compare.ready_to_sign_delta)} · Zur Einreichung {formatDelta(filingData.version_compare.ready_to_upload_delta)}</p>
                                        <p className="mt-1 text-xs text-slate-500">Fortschritt {formatDelta(filingData.version_compare.completion_progress_delta)}% · Aufgaben {formatDelta(filingData.version_compare.open_tasks_delta || 0)} · Neue Rueckmeldungen {formatDelta(filingData.version_compare.fresh_response_delta || 0)}</p>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Bibliothek</p>
                                        <p className="mt-2 text-sm text-slate-900">Geprueft {formatDelta(filingData.version_compare.library_checked_delta)} · unklar {formatDelta(filingData.version_compare.library_uncertain_delta)}</p>
                                        <p className="mt-1 text-xs text-slate-500">Nur beschrieben {formatDelta(filingData.version_compare.described_only_delta)} · Anfragen {formatDelta(filingData.version_compare.active_requests_delta || 0)} · Bald faellig {formatDelta(filingData.version_compare.due_soon_requests_delta || 0)} · Ueberfaellig {formatDelta(filingData.version_compare.overdue_requests_delta || 0)}</p>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Operative Signale</p>
                                        <p className="mt-2 text-sm text-slate-900">Benachrichtigungen {formatDelta(filingData.version_compare.notification_delta || 0)} · Finale Pakete {formatDelta(filingData.version_compare.final_pack_delta || 0)}</p>
                                        <p className="mt-1 text-xs text-slate-500">Bewerber ohne Rueckmeldung {formatDelta(filingData.version_compare.stale_candidate_requests_delta || 0)} · Offener Pruefstand {formatDelta(filingData.version_compare.stale_employer_review_delta || 0)}</p>
                                      </div>
                                    </div>
                                    <div className="mt-4 grid gap-4 xl:grid-cols-3">
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Dokumentaenderungen</p>
                                        <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                          {(filingData.version_compare.changed_documents || []).slice(0, 4).map((entry) => (
                                            <li key={`compare-doc-${entry.document_id}`}>
                                              {entry.title}: {entry.deltas.map((delta) => `${delta.field} ${delta.previous || "n/a"} -> ${delta.current || "n/a"}`).join(" · ")}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Behoerdenauflosung</p>
                                        <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                          {(filingData.version_compare.authority_resolution_changes || []).slice(0, 4).map((entry) => (
                                            <li key={`compare-authority-${entry.authority_id}`}>
                                              {entry.title}: {entry.previous_state || "n/a"} / {entry.previous_city || "n/a"} -> {entry.current_state || "n/a"} / {entry.current_city || "n/a"}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Pack-Reife</p>
                                        <p className="mt-2 text-sm text-slate-700">
                                          {filingData.version_compare.current_pack_label
                                            ? `${filingData.version_compare.previous_pack_label || "n/a"} -> ${filingData.version_compare.current_pack_label}`
                                            : "Noch keine Reifeaenderung hinterlegt."}
                                        </p>
                                        <p className="mt-2 text-xs text-slate-500">
                                          Naechster Schritt {filingData.version_compare.ready_for_next_step_changed ? "hat sich geaendert" : "unveraendert"}.
                                        </p>
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <p className="mt-4 text-sm text-slate-600">
                                    Sobald mindestens ein frueherer Kit-Stand existiert, zeigt diese Ansicht direkt die arbeitsrelevanten Unterschiede zum aktuellen Paket.
                                  </p>
                                )}
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Pruef- und Einreichungsworkflow</p>
                                    <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.review_workflow?.overall_label}</p>
                                    <p className="mt-1 text-sm text-slate-600">
                                      {filingData.review_workflow?.case_ready_for_next_authority_step
                                        ? "Der Fall ist fuer den naechsten behoerdlichen Upload-/Weitergabeschritt vorbereitet."
                                        : "Vor der naechsten Behoerdenstufe sind noch Pruef-, Signatur- oder Nachweis-Schritte offen."}
                                    </p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                    Stufe: <span className="font-semibold text-slate-900">{filingData.review_workflow?.overall_stage}</span>
                                  </div>
                                </div>
                                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                                  {Object.entries(filingData.review_workflow?.buckets || {}).map(([bucketKey, bucketEntries]) => {
                                    const meta = getReviewBucketMeta(bucketKey);
                                    return (
                                      <div key={bucketKey} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${meta.tone}`}>{meta.label}</span>
                                        <p className="mt-3 text-2xl font-semibold text-slate-900">{bucketEntries.length}</p>
                                        <ul className="mt-2 list-disc pl-5 text-sm text-slate-600">
                                          {bucketEntries.slice(0, 3).map((entry) => (
                                            <li key={`${bucketKey}-${entry.document_id}`}>{entry.title}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    );
                                  })}
                                </div>
                                {filingData.review_workflow?.next_actions?.length ? (
                                  <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Naechste Schritte</p>
                                    <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                      {filingData.review_workflow.next_actions.map((entry) => (
                                        <li key={entry}>{entry}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Completion Workflow</p>
                                    <p className="mt-2 text-lg font-semibold text-slate-900">
                                      {filingData.completion_workflow?.ready_for_next_step
                                        ? "Naechster Behoerdenschritt ist vorbereitet"
                                        : "Es gibt noch aktive Blocker vor dem naechsten Behoerdenschritt"}
                                    </p>
                                    <p className="mt-1 text-sm text-slate-600">
                                      Fehlende Nachweise und manuelle Schritte werden hier in eine konkrete Abarbeitung fuer Arbeitgeber, Bewerber und externe Stellen uebersetzt.
                                    </p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                    Fortschritt: <span className="font-semibold text-slate-900">{filingData.completion_workflow?.progress_percent || 0}%</span>
                                  </div>
                                </div>
                                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Jetzt signierbar</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.completion_workflow?.sign_now_count || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Jetzt uploadbereit</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.completion_workflow?.done_now_count || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Blocker</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.completion_workflow?.blocking_count || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Bearbeiterrollen</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.completion_workflow?.responsibility_summary?.length || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Abschlussstatus</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">
                                      {filingData.completion_workflow?.ready_for_next_step ? "bereit fuer naechsten Schritt" : "noch nicht abgeschlossen"}
                                    </p>
                                  </div>
                                </div>
                                {filingData.completion_workflow?.summary_lines?.length ? (
                                  <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <ul className="list-disc pl-5 text-sm text-slate-700">
                                      {filingData.completion_workflow.summary_lines.map((line) => (
                                        <li key={line}>{line}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                                <div className="mt-4 grid gap-4 xl:grid-cols-[0.95fr,1.05fr]">
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Jetzt erledigbare Schritte</p>
                                    <div className="mt-3 space-y-3">
                                      {filingData.completion_workflow?.immediate_actions?.length ? filingData.completion_workflow.immediate_actions.map((entry) => (
                                        <div key={`completion-immediate-${entry.document_id}`} className="rounded-lg border border-white bg-white p-3">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-semibold text-slate-900">{entry.title}</p>
                                            <span className={`rounded-full border px-2 py-1 text-xs ${getReviewBucketMeta(entry.review_bucket).tone}`}>
                                              {entry.status_label || getReviewBucketMeta(entry.review_bucket).label}
                                            </span>
                                          </div>
                                          <p className="mt-1 text-sm text-slate-600">{entry.action_label}</p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            Verantwortlich: {entry.owner_label}
                                            {entry.signature_required ? " · Unterschrift erforderlich" : ""}
                                          </p>
                                        </div>
                                      )) : (
                                        <p className="text-sm text-slate-600">Aktuell keine sofort erledigbaren Schritte sichtbar.</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Arbeitslanes nach Verantwortlichen</p>
                                    <div className="mt-3 space-y-3">
                                      {filingData.completion_workflow?.task_lanes?.length ? filingData.completion_workflow.task_lanes.map((lane) => (
                                        <div key={`lane-${lane.key}`} className="rounded-lg border border-white bg-white p-3">
                                          <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                              <span className={`rounded-full border px-2 py-1 text-xs ${getResponsibilityMeta(lane.key).tone}`}>{lane.label}</span>
                                              <p className="text-sm text-slate-600">offen {lane.open_count} · blocker {lane.blocking_count} · ready now {lane.ready_now_count}</p>
                                            </div>
                                            <span className="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-500">{lane.total_count}</span>
                                          </div>
                                          <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                            {lane.tasks.slice(0, 3).map((task) => (
                                              <li key={`lane-${lane.key}-${task.document_id}`}>{task.title} - {task.action_label}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )) : (
                                        <p className="text-sm text-slate-600">Noch keine Verantwortlichkeits-Lanes berechnet.</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-4 grid gap-4 xl:grid-cols-[1.1fr,0.9fr]">
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Blockierende Aufgaben</p>
                                    <div className="mt-3 space-y-3">
                                      {filingData.completion_workflow?.next_blockers?.length ? filingData.completion_workflow.next_blockers.map((entry) => (
                                        <div key={`completion-${entry.document_id}`} className="rounded-lg border border-white bg-white p-3">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-semibold text-slate-900">{entry.title}</p>
                                            <span className={`rounded-full border px-2 py-1 text-xs ${getReviewBucketMeta(entry.review_bucket).tone}`}>
                                              {getReviewBucketMeta(entry.review_bucket).label}
                                            </span>
                                          </div>
                                          <p className="mt-1 text-sm text-slate-600">{entry.action_label}</p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            Verantwortlich: {entry.responsibility?.label}
                                            {entry.signature_required ? " · Unterschrift erforderlich" : ""}
                                          </p>
                                          {entry.notes?.length ? (
                                            <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                              {entry.notes.slice(0, 3).map((note) => (
                                                <li key={`${entry.document_id}-${note}`}>{note}</li>
                                              ))}
                                            </ul>
                                          ) : null}
                                        </div>
                                      )) : (
                                        <p className="text-sm text-slate-600">Keine blockierenden Aufgaben mehr.</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Verantwortlichkeiten</p>
                                    <div className="mt-3 space-y-3">
                                      {filingData.completion_workflow?.responsibility_summary?.length ? filingData.completion_workflow.responsibility_summary.map((entry) => (
                                        <div key={entry.label} className="rounded-lg border border-white bg-white p-3">
                                          <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                              <span className={`rounded-full border px-2 py-1 text-xs ${getResponsibilityMeta(entry.key).tone}`}>{entry.label}</span>
                                              <p className="text-xs text-slate-500">Blocker {entry.blocking_count || 0} · ready now {entry.ready_now_count || 0}</p>
                                            </div>
                                            <span className="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-500">{entry.count}</span>
                                          </div>
                                        </div>
                                      )) : (
                                        <p className="text-sm text-slate-600">Noch keine Completion-Verantwortlichkeiten berechnet.</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="grid gap-4 xl:grid-cols-[1.2fr,0.8fr]">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Einreichungsreihenfolge</p>
                                  <div className="mt-3 space-y-3">
                                    {(filingData.employer_ready_pack?.ordered_steps || []).map((step) => (
                                      <div key={step.step} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Schritt {step.step}</p>
                                        <p className="mt-1 font-semibold text-slate-900">{step.title}</p>
                                        <p className="mt-1 text-sm text-slate-600">{step.description}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Behoerdenkontaktblock</p>
                                  <div className="mt-3 space-y-3">
                                    {(filingData.employer_ready_pack?.authority_contact_block || []).map((entry) => (
                                      <div key={`${entry.title}-${entry.city || entry.state}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="font-semibold text-slate-900">{entry.title}</p>
                                        <p className="mt-1 text-sm text-slate-600">{entry.state}{entry.city ? ` / ${entry.city}` : ""}</p>
                                        <a href={entry.source_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-sky-700">
                                          Offizielle Quelle
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Foederale Aufloesung</p>
                                <p className="mt-3 text-sm text-slate-700">{filingData.jurisdiction_resolution?.summary}</p>
                                <div className="mt-3 grid gap-3 xl:grid-cols-3">
                                  {(filingData.jurisdiction_resolution?.layers || []).map((layer) => (
                                    <div key={layer.level} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{layer.level}</p>
                                      <p className="mt-2 font-semibold text-slate-900">{layer.title}</p>
                                      <p className="mt-1 text-sm text-slate-600">{layer.effect}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {filingData.document_refresh ? (
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Letzte Bibliotheksaktualisierung</p>
                                  <p className="mt-2 text-slate-700">
                                    {filingData.document_refresh.updated_count} Dokumente aktualisiert, {filingData.document_refresh.skipped_count} uebersprungen, {filingData.document_refresh.error_count} Fehler.
                                  </p>
                                </div>
                              ) : null}

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Dokumentenfrische und Bibliotheksstand</p>
                                    <p className="mt-2 text-sm text-slate-600">
                                      Sichtbar ist, welche offiziellen Unterlagen lokal geprueft, nur beschrieben oder aktuell unklar sind.
                                    </p>
                                  </div>
                                  <div className="text-sm text-slate-500">
                                    Lokal geprueft: {filingData.document_library_status?.current_locally_checked_documents || 0}
                                                <div>Letzte Pruefung: {filingData.document_library_status?.last_checked_at || "k.A."}</div>
                                                <div>Letzter Download: {filingData.document_library_status?.last_downloaded_at || "k.A."}</div>
                                  </div>
                                </div>
                                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Aktuell lokal geprueft</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.document_library_status?.current_locally_checked_documents || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Aktualitaet unklar</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.document_library_status?.local_unknown_currency_documents || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Nur offiziell beschrieben</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.document_library_status?.officially_described_documents || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Refresh-faehig</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.document_library_status?.refreshable_documents || 0}</p>
                                  </div>
                                </div>
                                {(filingData.document_library_status?.uncertain_titles?.length || filingData.document_library_status?.described_only_titles?.length) ? (
                                  <div className="mt-4 grid gap-3 xl:grid-cols-2">
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Aktualitaet unklar</p>
                                      <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                        {(filingData.document_library_status?.uncertain_titles || []).slice(0, 6).map((title) => (
                                          <li key={`uncertain-${title}`}>{title}</li>
                                        ))}
                                      </ul>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Nur offiziell beschrieben</p>
                                      <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                        {(filingData.document_library_status?.described_only_titles || []).slice(0, 6).map((title) => (
                                          <li key={`described-${title}`}>{title}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                ) : null}
                              </div>

                              <div className="grid gap-4 xl:grid-cols-2">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Behoerdenliste</p>
                                  <div className="mt-3 space-y-3">
                                    {filingData.authority_matrix?.map((entry) => (
                                      <div key={`${entry.title}-${entry.level}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="font-semibold text-slate-900">{entry.title}</p>
                                        <p className="text-sm text-slate-600">{entry.scope}</p>
                                        <p className="mt-1 text-xs text-slate-500">{entry.resolved_state || "Bund"} · {entry.resolved_city || "ohne Ortsbindung"}</p>
                                        <a href={entry.source_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-sky-700">
                                          Quelle oeffnen
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Fehlende / manuelle Unterlagen</p>
                                  <div className="mt-3 space-y-3">
                                    {filingData.missing_documents?.length ? filingData.missing_documents.map((entry) => (
                                      <div key={entry.document_id} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                        <p className="font-semibold text-slate-900">{entry.title}</p>
                                        <p className="text-sm text-slate-600">{entry.status}</p>
                                        <p className="mt-1 text-xs text-slate-500">
                                          Verantwortlich: {entry.responsibility?.label || "manuell klaeren"}
                                          {entry.signature_required ? " · Unterschrift erforderlich" : ""}
                                          {entry.review_bucket ? ` · ${getReviewBucketMeta(entry.review_bucket).label}` : ""}
                                        </p>
                                        {entry.responsibility?.detail ? (
                                          <p className="mt-1 text-xs text-slate-500">{entry.responsibility.detail}</p>
                                        ) : null}
                                        {entry.notes?.length ? (
                                          <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                            {entry.notes.map((note) => (
                                              <li key={`${entry.document_id}-${note}`}>{note}</li>
                                            ))}
                                          </ul>
                                        ) : null}
                                      </div>
                                    )) : (
                                      <p className="text-sm text-slate-600">Keine zusaetzlichen Missing-Docs erkannt.</p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Formular- und Dokumentenliste</p>
                                <div className="mt-3 grid gap-3 xl:grid-cols-2">
                                  {filingData.documents?.map((entry) => (
                                    <div key={entry.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                        <div>
                                          <p className="font-semibold text-slate-900">{entry.title}</p>
                                          <p className="text-sm text-slate-600">{entry.authority}</p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            {entry.level} · {entry.checklist_status?.status} · {entry.usable_mode}
                                          </p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            Letzter Download: {entry.downloaded_at || "noch nicht lokal"} · Letzte Pruefung: {entry.last_checked_at || "k.A."}
                                          </p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            PDF-Felder: {entry.pdf_field_count ?? 0} · Auto: {entry.autofill_field_count ?? 0} · Manuell: {entry.manual_field_count ?? 0}
                                          </p>
                                          <p className="mt-1 text-xs text-slate-500">
                                          Abdeckung: {formatCoverageLevel(entry.field_coverage_level)} · Sicherheit: {entry.fillability_confidence ?? "k.A."}
                                            {entry.signature_required ? " · Unterschrift erforderlich" : ""}
                                          </p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {entry.download_url ? (
                                            <Button
                                              variant="outline"
                                              className="dashboard-inline-button"
                                              onClick={() => downloadAuthorityDocument(row.id, entry)}
                                              disabled={detailState.exporting}
                                            >
                                              Offizielles Dokument
                                            </Button>
                                          ) : null}
                                          <span className={`rounded-full border px-2 py-1 text-xs ${getReviewBucketMeta(entry.review_bucket).tone}`}>
                                            {getReviewBucketMeta(entry.review_bucket).label}
                                          </span>
                                          <span className="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-500">
                                            {entry.fillability_class === "fillable_pdf" ? "direkt vorbefuellbar" : entry.fillability_class === "structured_transfer_only" ? "Transferhilfe" : entry.fillability_class === "manual_only" ? "manuell" : "Referenz"}
                                          </span>
                                          {entry.freshness ? (
                                            <span className={`rounded-full border px-2 py-1 text-xs ${getFreshnessMeta(entry.freshness.freshness_status).tone}`}>
                                              {entry.freshness.label}
                                            </span>
                                          ) : null}
                                        </div>
                                      </div>
                                      {entry.fillability_reason ? (
                                        <p className="mt-2 text-sm text-slate-600">{entry.fillability_reason}</p>
                                      ) : null}
                                      {entry.review_note ? (
                                        <p className="mt-2 text-sm text-slate-600">{entry.review_note}</p>
                                      ) : null}
                                      {entry.plausibility_warnings?.length ? (
                                        <ul className="mt-2 list-disc pl-5 text-sm text-amber-700">
                                          {entry.plausibility_warnings.map((note) => <li key={note}>{note}</li>)}
                                        </ul>
                                      ) : null}
                                      {entry.checklist_status?.notes?.length ? (
                                        <ul className="mt-2 list-disc pl-5 text-sm text-slate-600">
                                          {entry.checklist_status.notes.map((note) => <li key={note}>{note}</li>)}
                                        </ul>
                                      ) : null}
                                      {entry.review_requirements?.length ? (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                          {entry.review_requirements.map((requirement) => (
                                            <span key={`${entry.id}-${requirement}`} className="rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-500">
                                              {requirement}
                                            </span>
                                          ))}
                                        </div>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="grid gap-4 xl:grid-cols-2">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Arbeitgeber-Checkliste</p>
                                  <ul className="mt-3 list-disc pl-5 text-sm text-slate-700">
                                    {filingData.employer_checklist?.map((entry) => (
                                      <li key={entry.title}>{entry.title} - {entry.status}</li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Bewerber-Checkliste</p>
                                  <ul className="mt-3 list-disc pl-5 text-sm text-slate-700">
                                    {filingData.candidate_checklist?.map((entry) => (
                                      <li key={entry.title}>{entry.title} - {entry.status}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              <div className="grid gap-4 xl:grid-cols-2">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Offene Filing-Aufgaben</p>
                                      <p className="mt-2 text-sm text-slate-600">
                                        Echte Arbeitsobjekte mit persistentem Status, Notiz und Candidate-Request-Schleife.
                                      </p>
                                    </div>
                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                      {filingData.task_board?.open_tasks?.length || 0} offen
                                    </span>
                                  </div>
                                  <div className="mt-3 space-y-3">
                                    {filingData.task_board?.open_tasks?.length ? filingData.task_board.open_tasks.slice(0, 10).map((task) => (
                                      <div key={`task-${task.task_id}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                          <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                              <p className="font-semibold text-slate-900">{task.title}</p>
                                              <span className={`rounded-full border px-2 py-1 text-[11px] ${getResponsibilityMeta(task.owner_role).tone}`}>
                                                {task.owner_role}
                                              </span>
                                              <span className={`rounded-full border px-2 py-1 text-[11px] ${getReviewBucketMeta(task.source_bucket || "manual_only").tone}`}>
                                                {task.source_bucket || task.source_type}
                                              </span>
                                            </div>
                                            <p className="mt-1 text-sm text-slate-600">
                                              {formatTaskStatus(task.status)} · Prioritaet {task.priority || "normal"}
                                              {task.document_id ? ` · ${task.document_id}` : ""}
                                            </p>
                                            {task.description ? <p className="mt-2 text-sm text-slate-600">{task.description}</p> : null}
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            <Button variant="outline" className="dashboard-inline-button" onClick={() => updateAuthorityTask(row.id, task, "complete")}>Erledigt</Button>
                                            <Button variant="outline" className="dashboard-inline-button" onClick={() => updateAuthorityTask(row.id, task, task.status === "blocked" ? "reopen" : "block")}>
                                              {task.status === "blocked" ? "Entblocken" : "Blockieren"}
                                            </Button>
                                            <Button variant="outline" className="dashboard-inline-button" onClick={() => updateAuthorityTask(row.id, task, "progress")}>In Arbeit</Button>
                                            {task.requires_candidate_input || task.owner_role === "candidate" ? (
                                              <Button variant="outline" className="dashboard-inline-button" onClick={() => createCandidateRequest(row.id, task, true)}>Nachforderung senden</Button>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="mt-3 flex flex-col gap-2 lg:flex-row">
                                          <textarea
                                            className="min-h-[74px] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none"
                                            placeholder="Completion-Notiz fuer diesen Task"
                                            value={detailState.taskNotes?.[String(task.task_id)] ?? task.completion_note ?? ""}
                                            onChange={(event) => handleTaskNoteChange(row.id, task.task_id, event.target.value)}
                                          />
                                          <Button variant="outline" className="dashboard-inline-button self-start" onClick={() => updateAuthorityTask(row.id, task, "note")}>
                                            Notiz speichern
                                          </Button>
                                        </div>
                                      </div>
                                    )) : (
                                      <p className="text-sm text-slate-600">Keine offenen Filing-Aufgaben mehr.</p>
                                    )}
                                  </div>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Candidate Request Loop</p>
                                      <p className="mt-2 text-sm text-slate-600">
                                        Filing-spezifischer Request-Log fuer fehlende Angaben, Uploads, Uebersetzungen und Signaturen.
                                      </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                        {filingData.candidate_request_board?.active_requests?.length || 0} aktiv
                                      </span>
                                      <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700">
                                        {filingData.candidate_request_board?.due_soon_count || 0} bald faellig
                                      </span>
                                      <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700">
                                        {filingData.candidate_request_board?.overdue_count || 0} ueberfaellig
                                      </span>
                                      <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">
                                  {filingData.candidate_response_board?.pending_review_count || 0} in Pruefung
                                      </span>
                                    </div>
                                  </div>
                                  <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Awaiting candidate</p>
                                      <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.candidate_request_board?.awaiting_candidate_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Warten auf Pruefung</p>
                                      <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.candidate_request_board?.needs_review_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Stale candidate</p>
                                      <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.candidate_request_board?.stale_candidate_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Fresh responses</p>
                                      <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.candidate_request_board?.fresh_response_count || 0}</p>
                                    </div>
                                  </div>
                                  {filingData.candidate_request_board?.summary_lines?.length ? (
                                    <ul className="mt-3 list-disc pl-5 text-sm text-slate-700">
                                      {filingData.candidate_request_board.summary_lines.map((line) => (
                                        <li key={line}>{line}</li>
                                      ))}
                                    </ul>
                                  ) : null}
                                  {filingData.delivery_board ? (
                                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Delivery Audit</p>
                                      <p className="mt-2 text-sm text-slate-900">
                                        Mails gesendet {filingData.delivery_board.email_sent_count || 0} · Pending {filingData.delivery_board.email_pending_count || 0} · Fehler {filingData.delivery_board.delivery_failure_count || 0} · Retry {filingData.delivery_board.retry_count || 0}
                                      </p>
                                      {filingData.delivery_board.summary_lines?.length ? (
                                        <p className="mt-1 text-xs text-slate-500">{filingData.delivery_board.summary_lines.slice(0, 2).join(" · ")}</p>
                                      ) : null}
                                    </div>
                                  ) : null}
                                  {filingData.notification_board?.employer_notifications?.length ? (
                                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Letzte Filing-Hinweise</p>
                                      <div className="mt-2 space-y-2">
                                        {filingData.notification_board.employer_notifications.slice(0, 4).map((entry) => (
                                          <div key={`notification-${entry.notification_id}`} className="rounded-lg border border-white bg-white px-3 py-2 text-sm text-slate-600">
                                            <strong className="text-slate-900">{entry.title}</strong>
                                            <p className="mt-1">{entry.message}</p>
                                            <p className="mt-1 text-xs text-slate-500">{formatDateTime(entry.created_at)} · {entry.notification_type}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                  <div className="mt-3 space-y-3">
                                    {filingData.candidate_request_board?.all_requests?.length ? filingData.candidate_request_board.all_requests.slice(0, 10).map((requestEntry) => (
                                      <div
                                        key={`request-${requestEntry.request_id}`}
                                        className={`rounded-lg border bg-slate-50 p-3 ${String(requestEntry.request_id) === String(requestedRequestId) ? "border-teal-300 ring-2 ring-teal-100" : "border-slate-200"}`}
                                      >
                                        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                          <div>
                                            <p className="font-semibold text-slate-900">{requestEntry.title}</p>
                                            <p className="mt-1 text-sm text-slate-600">
                                              {formatRequestStatus(requestEntry.status)} · Dringlichkeit {formatUrgencyLabel(requestEntry.urgency || "medium")}
                                            </p>
                                            {requestEntry.response_status ? (
                                              <p className="mt-1 text-sm text-slate-600">{formatResponseStatus(requestEntry.response_status)}</p>
                                            ) : null}
                                            <div className="mt-2 flex flex-wrap gap-2">
                                              <span className={`rounded-full border px-2 py-1 text-[11px] ${getDeadlineTone(requestEntry)}`}>
                                          {requestEntry.overdue ? "ueberfaellig" : requestEntry.due_soon ? "bald faellig" : requestEntry.awaiting_employer_review ? "wartet auf Pruefung" : "im Lauf"}
                                              </span>
                                              {requestEntry.stale_candidate ? (
                                                <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-700">
                                                  candidate silent
                                                </span>
                                              ) : null}
                                              {requestEntry.stale_employer_review ? (
                                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
                                              Pruefung offen
                                                </span>
                                              ) : null}
                                            </div>
                                            {requestEntry.request_reason ? (
                                              <p className="mt-2 text-sm text-slate-600">{requestEntry.request_reason}</p>
                                            ) : null}
                                            <p className="mt-2 text-xs text-slate-500">
                                              Faellig {formatDateTime(requestEntry.due_at)} · Reminder {formatDateTime(requestEntry.next_reminder_at)} · Letzte Aktivitaet {formatDateTime(requestEntry.last_activity_at)}
                                            </p>
                                            <p className="mt-1 text-xs text-slate-500">
                                              Offen seit {formatRelativeDays(requestEntry.open_days)} · Rest {formatRelativeDays(requestEntry.days_remaining)} · Reminder gesendet {requestEntry.reminder_count || 0}x
                                            </p>
                                            <p className="mt-1 text-xs text-slate-500">
                                              Letzte Mail {formatDateTime(requestEntry.delivery_summary?.last_email_delivery_at)} · Status {requestEntry.delivery_summary?.last_email_status || "in_app only / pending"} · Retry {requestEntry.delivery_summary?.retry_count || 0}
                                            </p>
                                            {requestEntry.delivery_summary?.last_email_error ? (
                                              <p className="mt-1 text-xs text-rose-600">Delivery-Fehler: {requestEntry.delivery_summary.last_email_error}</p>
                                            ) : null}
                                            {requestEntry.delivery_summary?.last_skip_reason ? (
                                              <p className="mt-1 text-xs text-slate-500">Mail-Fallback: {requestEntry.delivery_summary.last_skip_reason}</p>
                                            ) : null}
                                            {requestEntry.latest_response_summary?.preview_text ? (
                                              <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
                                          <strong className="text-slate-900">Letzte Bewerberrueckmeldung:</strong> {requestEntry.latest_response_summary.preview_text}
                                              </div>
                                            ) : null}
                                            {requestEntry.latest_response_summary?.verification_reference_id ? (
                                              <p className="mt-2 text-xs text-slate-500">
                                                Verification Ref #{requestEntry.latest_response_summary.verification_reference_id}
                                              </p>
                                            ) : null}
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            {requestEntry.status === "draft" ? (
                                              <Button variant="outline" className="dashboard-inline-button" onClick={() => updateCandidateRequest(row.id, requestEntry, "sent")}>Senden</Button>
                                            ) : null}
                                            {["sent", "viewed"].includes(requestEntry.status) ? (
                                              <Button
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => updateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_received" })}
                                              >
                                                Antwort erhalten
                                              </Button>
                                            ) : null}
                                            {requestEntry.status === "answered" ? (
                                              <Button
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => updateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_under_review" })}
                                              >
                                                In Pruefung
                                              </Button>
                                            ) : null}
                                            {["answered", "viewed", "sent"].includes(requestEntry.status) ? (
                                              <Button
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => updateCandidateRequest(row.id, requestEntry, "fulfilled", { response_status: "response_accepted" })}
                                              >
                                                Akzeptieren
                                              </Button>
                                            ) : null}
                                            {requestEntry.status === "answered" ? (
                                              <Button
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => updateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_incomplete" })}
                                              >
                                                Unvollstaendig
                                              </Button>
                                            ) : null}
                                            {requestEntry.status === "answered" ? (
                                              <Button
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => updateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_rejected" })}
                                              >
                                                Nacharbeit
                                              </Button>
                                            ) : null}
                                            {!["fulfilled", "obsolete", "expired"].includes(requestEntry.status) ? (
                                              <Button variant="outline" className="dashboard-inline-button" onClick={() => updateCandidateRequest(row.id, requestEntry, "obsolete")}>Obsolet</Button>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                    )) : (
                                      <p className="text-sm text-slate-600">Noch keine Filing-Anfragen gespeichert.</p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {filingData.form_prefill_guides?.length ? (
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Vorbereitete Feldzuordnung / Pre-Fill Sheets</p>
                                  <div className="mt-3 space-y-3">
                                    {filingData.form_prefill_guides.map((guide) => (
                                      <div key={guide.document_id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                          <p className="font-semibold text-slate-900">{guide.document_title}</p>
                                          <span className="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-500">{guide.preparation_mode}</span>
                                        </div>
                                        {guide.employer_review_note ? (
                                          <p className="mt-2 text-sm text-slate-600">{guide.employer_review_note}</p>
                                        ) : null}
                                        <div className="mt-3 space-y-3">
                                          {Object.entries(
                                            guide.prefill_fields.reduce((acc, field) => {
                                              const key = field.section || "Allgemein";
                                              if (!acc[key]) acc[key] = [];
                                              acc[key].push(field);
                                              return acc;
                                            }, {})
                                          ).map(([section, fields]) => (
                                            <div key={`${guide.document_id}-${section}`} className="rounded-lg border border-white bg-white p-3">
                                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{section}</p>
                                              <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                                {fields.map((field) => (
                                                  <li key={`${guide.document_id}-${section}-${field.field_name}`}>
                                                    {field.field_name}: {field.target_value} · Quelle: {field.source_label} ({field.automation})
                                                    {field.required ? " · Pflichtfeld" : " · optional"}
                                                    {field.review_action ? ` · ${field.review_action}` : ""}
                                                    {field.manual_reason ? ` · ${field.manual_reason}` : ""}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          ))}
                                        </div>
                                        {guide.plausibility_warnings?.length ? (
                                          <ul className="mt-2 list-disc pl-5 text-sm text-amber-700">
                                            {guide.plausibility_warnings.map((warning) => (
                                              <li key={warning}>{warning}</li>
                                            ))}
                                          </ul>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Generierte Arbeitgeber-Unterlagen</p>
                                  <p className="text-xs text-slate-500">
                                    {filingData.generated_assets?.assets?.length || 0} Dateien im aktuellen Paket
                                  </p>
                                </div>
                                <div className="mt-3 grid gap-3 xl:grid-cols-2">
                                  {filingData.generated_assets?.assets?.length ? filingData.generated_assets.assets.map((asset) => (
                                    <div key={asset.asset_id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                        <div>
                                          <p className="font-semibold text-slate-900">{asset.title}</p>
                                          <p className="mt-1 text-sm text-slate-600">{asset.asset_type} · {asset.format}</p>
                                        </div>
                                        <Button
                                          variant="outline"
                                          className="dashboard-inline-button"
                                          onClick={() => downloadPreparedAsset(row.id, asset)}
                                          disabled={detailState.exporting}
                                        >
                                          Vorbereitete Datei
                                        </Button>
                                      </div>
                                    </div>
                                  )) : (
                                    <p className="text-sm text-slate-600">Noch keine vorbereiteten Pack-Dateien vorhanden.</p>
                                  )}
                                </div>
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Persistente Kit-Historie</p>
                                  <p className="text-xs text-slate-500">BA {filingData.classification?.ba_consent_required ? "ja" : "nein"} · Vollmacht {filingData.classification?.power_of_attorney_required ? "ja" : "nein"}</p>
                                </div>
                                <div className="mt-3 space-y-3">
                                  {filingData.history?.length ? filingData.history.map((entry) => (
                                    <div key={`${entry.case_id}-${entry.version_no}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <p className="font-semibold text-slate-900">Version {entry.version_no} · {formatHistoryAction(entry.action_type)}</p>
                                        <span className="text-xs text-slate-500">{entry.created_at}</span>
                                      </div>
                                      <p className="mt-1 text-sm text-slate-600">
                                        {entry.classification?.procedure_explainer?.label || entry.classification?.procedure || "Pfad"} · {entry.classification?.federal_state || "ohne Bundesland"} · {entry.classification?.city || "ohne Stadt"}
                                      </p>
                                      {entry.completion_workflow ? (
                                        <p className="mt-1 text-xs text-slate-500">
                                          Fortschritt: {entry.completion_workflow.progress_percent || 0}% · signierbar {entry.completion_workflow.sign_now_count || 0} · uploadbereit {entry.completion_workflow.done_now_count || 0} · Blocker {entry.completion_workflow.blocking_count || 0}
                                        </p>
                                      ) : null}
                                      <div className="mt-3 grid gap-2 md:grid-cols-3 xl:grid-cols-6">
                                        {Object.entries(entry.history_summary?.review_buckets || {}).map(([bucketKey, count]) => {
                                          const meta = getReviewBucketMeta(bucketKey);
                                          return (
                                            <div key={`${entry.version_no}-${bucketKey}`} className="rounded-lg border border-white bg-white p-2">
                                              <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] ${meta.tone}`}>{meta.label}</span>
                                              <p className="mt-2 font-semibold text-slate-900">{count}</p>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <p className="mt-3 text-xs text-slate-500">
                                      Ausfuellbare PDFs: {entry.history_summary?.fillability?.fillable_pdf || 0} · Transferhilfen: {entry.history_summary?.fillability?.structured_transfer_only || 0} · Lokal geprueft: {entry.history_summary?.locally_checked_documents || 0} · Pack-Dateien: {entry.history_summary?.generated_assets_count || 0} · Offene Aufgaben: {entry.history_summary?.open_tasks_count || 0} · Aktive Anfragen: {entry.history_summary?.active_requests_count || 0} · Finale Pakete: {entry.history_summary?.final_pack_count || 0}
                                      </p>
                                      {entry.previous_version_delta ? (
                                        <div className="mt-3 rounded-lg border border-white bg-white p-3">
                                          <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Versionsvergleich zur Vorversion</p>
                                          <p className="mt-2 text-xs text-slate-500">
                                          Fehlende Nachweise {entry.previous_version_delta.missing_docs_delta >= 0 ? "+" : ""}{entry.previous_version_delta.missing_docs_delta} · Zur Einreichung {entry.previous_version_delta.ready_to_upload_delta >= 0 ? "+" : ""}{entry.previous_version_delta.ready_to_upload_delta} · Zur Unterschrift {entry.previous_version_delta.ready_to_sign_delta >= 0 ? "+" : ""}{entry.previous_version_delta.ready_to_sign_delta} · Ausfuellbare PDFs {entry.previous_version_delta.fillable_pdf_delta >= 0 ? "+" : ""}{entry.previous_version_delta.fillable_pdf_delta} · Aufgaben {entry.previous_version_delta.open_tasks_delta >= 0 ? "+" : ""}{entry.previous_version_delta.open_tasks_delta} · Anfragen {entry.previous_version_delta.active_requests_delta >= 0 ? "+" : ""}{entry.previous_version_delta.active_requests_delta} · Finale Pakete {entry.previous_version_delta.final_pack_delta >= 0 ? "+" : ""}{entry.previous_version_delta.final_pack_delta}
                                          </p>
                                          {entry.previous_version_delta.summary_lines?.length ? (
                                            <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                              {entry.previous_version_delta.summary_lines.map((line) => (
                                                <li key={`${entry.version_no}-${line}`}>{line}</li>
                                              ))}
                                            </ul>
                                          ) : null}
                                          <div className="mt-3 grid gap-3 xl:grid-cols-3">
                                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Dokumente</p>
                                              <p className="mt-2 text-xs text-slate-500">+ {entry.previous_version_delta.document_changes?.added?.length || 0} · - {entry.previous_version_delta.document_changes?.removed?.length || 0}</p>
                                              <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                                {(entry.previous_version_delta.document_changes?.added || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-doc-add-${label}`}>Neu: {label}</li>
                                                ))}
                                                {(entry.previous_version_delta.document_changes?.removed || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-doc-rem-${label}`}>Entfallen: {label}</li>
                                                ))}
                                              </ul>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Fehlende Nachweise</p>
                                              <p className="mt-2 text-xs text-slate-500">+ {entry.previous_version_delta.missing_document_changes?.added?.length || 0} · - {entry.previous_version_delta.missing_document_changes?.removed?.length || 0}</p>
                                              <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                                {(entry.previous_version_delta.missing_document_changes?.added || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-missing-add-${label}`}>Neu: {label}</li>
                                                ))}
                                                {(entry.previous_version_delta.missing_document_changes?.removed || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-missing-rem-${label}`}>Geschlossen: {label}</li>
                                                ))}
                                              </ul>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Behoerden</p>
                                              <p className="mt-2 text-xs text-slate-500">+ {entry.previous_version_delta.authority_changes?.added?.length || 0} · - {entry.previous_version_delta.authority_changes?.removed?.length || 0}</p>
                                              <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                                {(entry.previous_version_delta.authority_changes?.added || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-authority-add-${label}`}>Neu: {label}</li>
                                                ))}
                                                {(entry.previous_version_delta.authority_changes?.removed || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-authority-rem-${label}`}>Entfallen: {label}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  )) : (
                                    <p className="text-sm text-slate-600">Noch keine gespeicherte Kit-Historie vorhanden.</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : null}
                            </>
                          ) : null}
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Interne Entscheidungsvorbereitung</p>
                              <p className="mt-2 text-slate-600">
                                Der Entscheidungsstatus bleibt jetzt nur zusammen mit Pflichtkommentar und nachvollziehbarer Historie valide. Filing-Readiness wird dadurch nicht ueberschrieben.
                              </p>
                            </div>
                            <p className="text-xs text-slate-400">
                              Zuletzt aktualisiert: {detail.decision_preparation?.updated_at || "k.A."}
                            </p>
                          </div>

                          <div className="mt-4 grid gap-4 lg:grid-cols-[220px,1fr]">
                            <div>
                              <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-slate-400">
                                Entscheidungsstatus
                              </label>
                              <Select
                                value={detailState.form.decision_state}
                                onValueChange={(value) => handleDecisionFormChange(row.id, "decision_state", value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Status waehlen" />
                                </SelectTrigger>
                                <SelectContent>
                                  {DECISION_STATE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-slate-400">
                                Interne Notiz
                              </label>
                              <textarea
                                value={detailState.form.decision_note}
                                onChange={(event) => handleDecisionFormChange(row.id, "decision_note", event.target.value)}
                                maxLength={500}
                                rows={4}
                                placeholder="Kurze interne Einordnung zur Entscheidungsvorbereitung."
                                className="dashboard-field dashboard-textarea w-full"
                              />
                              <p className="mt-1 text-right text-xs text-slate-500">
                                {detailState.form.decision_note.length}/500
                              </p>
                              {decisionNoteRequired ? (
                                <p className={`mt-1 text-xs ${decisionNoteMissing ? "text-rose-600" : "text-slate-500"}`}>
                                  {decisionNoteMissing
                                    ? "Fuer diesen Entscheidungsstatus ist eine interne Notiz verpflichtend."
                                    : "Pflichtkommentar vorhanden."}
                                </p>
                              ) : null}
                            </div>
                          </div>

                          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <CCallout color={detailState.error ? "danger" : (decisionBlockers.length ? "warning" : "info")} className="mb-0 flex-1">
                              <strong>Decision Action Safety</strong>
                              <div>
                                Die gespeicherte Entscheidung dokumentiert nur den internen Review-Stand. Sie hebt keinen Filing- oder Nachweisblocker auf.
                              </div>
                              <div className="mt-2">
                                {detailState.error
                                  ? "Solange die Detailbuehne nicht fehlerfrei geladen ist, bleibt Speichern bewusst gesperrt."
                                  : decisionBlockers.length
                                  ? `Vor einer Freigabe zuerst sauber klaeren: ${decisionBlockers[0]}`
                                  : "Wenn du speicherst, bleibt der Fall im selben Employer-Arbeitsraum weiterfuehrbar."}
                              </div>
                              <div className="mt-2">
                                {decisionNoteMissing
                                  ? `Ohne Pflichtkommentar bleibt '${formatDecisionState(detailState.form.decision_state)}' absichtlich unspeicherbar.`
                                  : decisionSaveAllowed
                                  ? `Beim Speichern wird jetzt nur der interne Entscheidungsstand '${formatDecisionState(detailState.form.decision_state)}' mit Notiz zurueckgeschrieben.`
                                  : "Speichern bleibt erst zulaessig, wenn die Decision-Oberflaeche fehlerfrei geladen und lesbar ist."}
                              </div>
                            </CCallout>
                            <div className="flex flex-wrap gap-2">
                              <CLoadingButton
                                className="dashboard-action-button"
                                disabledOnLoading
                                loading={detailState.saving}
                                disabled={!decisionSaveAllowed}
                                onClick={() => saveDecisionPreparation(row.id)}
                              >
                                Entscheidung speichern
                              </CLoadingButton>
                            </div>
                          </div>

                          <CCallout
                            color={detailState.saving ? "info" : (detailState.flash ? "success" : (detailState.error ? "danger" : (decisionBlockers.length ? "warning" : "info")))}
                            className="mt-3 mb-0"
                          >
                            <strong>Decision Save Readback</strong>
                            <div>
                              {detailState.saving
                                ? `Speichert jetzt den internen Stand '${formatDecisionState(detailState.form.decision_state)}'.`
                                : detailState.flash
                                  ? `${detailState.flash} Aktueller interner Stand: ${formatDecisionState(detailState.form.decision_state)}.`
                                  : detailState.error
                                    ? "Kein Save erfolgt. Die Decision-Detailflaeche muss zuerst wieder fehlerfrei geladen sein."
                                  : `Vor dem Speichern bleibt der aktuelle Stand '${formatDecisionState(detailState.form.decision_state)}'.`}
                            </div>
                            <div className="mt-2">
                              {detailState.error
                                ? "Naechster sinnvoller Schritt: Detailzustand laden oder den aktiven Fehler bereinigen."
                                : decisionNoteMissing
                                  ? "Naechster sinnvoller Schritt: Pflichtkommentar ergaenzen und danach erneut speichern."
                                : decisionBlockers.length
                                ? `Naechster sinnvoller Schritt bleibt vorbereitend: ${decisionBlockers[0]}`
                                : `Naechster zulaessiger Schritt nach dem Save: ${filingActionContext}`}
                            </div>
                          </CCallout>

                          {detail.decision_preparation?.history?.length ? (
                            <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Entscheidungshistorie</p>
                              <div className="mt-3 space-y-2">
                                {detail.decision_preparation.history.slice(0, 4).map((entry, index) => (
                                  <div key={`decision-history-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <strong className="text-slate-900">{formatDecisionState(entry.decision_state || "new")}</strong>
                                      <span className="text-xs text-slate-500">{entry.created_at || "k.A."}</span>
                                    </div>
                                    <p className="mt-2 mb-0 text-sm text-slate-600">{entry.decision_note || "Ohne Notiz gespeichert."}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </CardContent>
      </Card>
          </div>

          <aside className="employer-workspace-rail">
            <section className="employer-rail-card">
              <div className="employer-rail-card__head">
                <div className="employer-rail-card__copy">
                  <span className="employer-rail-card__kicker">Anzeige im Fokus</span>
                  <h2>{selectedAd ? selectedAd.titel : "Noch keine Anzeige gewaehlt"}</h2>
                  <p>
                    {selectedAd
                      ? `${selectedAd.beruf_text || "Beruf offen"} · ${selectedAd.standort_ort || "Ort offen"}`
                      : "Die Anzeige steuert, welche Bewerbungen und Filing-Pfade diese Surface zeigt."}
                  </p>
                </div>
              </div>
              {selectedAd ? (
                <div className="employer-note-list">
                  <article className="employer-note-item">
                    <strong>Status</strong>
                    <p>{formatAdStatusLabel(selectedAd.status)} · zuletzt aktualisiert {formatAbsoluteDate(selectedAd.updated_at)}</p>
                  </article>
                  <article className="employer-note-item">
                    <strong>Naechste Route</strong>
                    <p>Bewerbungspruefung und Filing bleiben unter derselben Anzeige gebuendelt.</p>
                  </article>
                </div>
              ) : (
                <div className="employer-selection-note">
                  <strong>Anzeige waehlen</strong>
                  <p>Ohne Anzeige gibt es keinen belastbaren Bewerbungs- und Filing-Kontext.</p>
                </div>
              )}
            </section>

            <section className="employer-rail-card">
              <div className="employer-rail-card__head">
                <div className="employer-rail-card__copy">
                  <span className="employer-rail-card__kicker">Aktiver Fall</span>
                  <h2>{activeApplication ? `Bewerbung #${activeApplication.id}` : "Noch kein Fall geoeffnet"}</h2>
                  <p>
                    {activeApplication
                      ? `Status ${formatDecisionState(activeApplication.status)} · zuletzt aktualisiert ${formatAbsoluteDate(activeApplication.updated_at)}`
                      : "Oeffne einen Fall, damit Status, Evidence und Filing im selben Arbeitsraum sichtbar werden."}
                  </p>
                </div>
              </div>
              {activeFilingData ? (
                <div className="employer-note-list">
                  <article className="employer-note-item">
                    <strong>Filing-Stand</strong>
                    <p>{activeFilingData.submission_pack_state?.current_stage_label || "Arbeitsstand"} · {filingOpenPointCount} offene Punkte</p>
                  </article>
                  <article className="employer-note-item">
                    <strong>Pack-Reife</strong>
                    <p>{activeFilingData.review_workflow?.overall_label || "wird berechnet"} · Blocker {activeFilingData.completion_workflow?.blocking_count || 0}</p>
                  </article>
                </div>
              ) : (
                <div className="employer-selection-note">
                  <strong>Arbeitsraum nach Bedarf</strong>
                  <p>Filing wird erst geladen, wenn du einen konkreten Fall aufklappst. So bleibt die Review-Flaeche ruhig.</p>
                </div>
              )}
            </section>

            <section className="employer-rail-card">
              <div className="employer-rail-card__head">
                <div className="employer-rail-card__copy">
                  <span className="employer-rail-card__kicker">Trainingskontext</span>
                  <h2>{isTraining ? "Gleiche UI, synthetische Daten" : "Live-Betrieb ohne Zweit-UI"}</h2>
                  <p>
                    {isTraining
                      ? "Der Trainingsfall nutzt dieselbe Surface. Unterschiede liegen nur im Datenadapter und im Modus."
                      : "Die Surface fuehrt reale Bewerbungen und reale Folgehandlungen, ohne Trainingslogik im Vordergrund."}
                  </p>
                </div>
              </div>
              {isTraining && trainingNextStep ? (
                <div className="employer-selection-note">
                  <strong>{trainingNextStep.title}</strong>
                  <p>{trainingNextStep.detail}</p>
                  <div className="employer-selection-note__actions">
                    <Button
                      variant="outline"
                      className="dashboard-inline-button"
                      onClick={openTrainingStep}
                    >
                      {trainingNextStep.buttonLabel}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="employer-selection-note">
                  <strong>Keine Trainingsueberlagerung</strong>
                  <p>Trainingslaeufe werden nur fuer die gewaehlt Anzeige zugeschaltet und bleiben klar als Modus gekennzeichnet.</p>
                </div>
              )}
            </section>
          </aside>
        </div>
      </div>
    </CockpitShell>
  );
};

export default StbApplications;
